/**
 * Boot and integration. M0a: she moves.
 *
 * The pieces this wires together are deliberately independent of each other —
 * the controller knows no scene graph, the camera knows no physics, the mesh
 * knows nothing at all. This file is the only place that knows about all three,
 * which is what lets each be replaced or tested on its own.
 *
 * Order matters inside `fixedUpdate`, and it is not arbitrary:
 *   1. resolve the camera-relative move basis (camera is the frame of reference)
 *   2. step the character controller
 *   3. step physics
 *   4. `input.consume()` — LAST, so edge-triggered actions survive exactly one tick
 *
 * Animation runs through `src/anim/`: `RosalieRig` and `AnimationPlayer` pose
 * her mesh from `Pose` deltas each render step, addressed by
 * `findRosalieParts()`.
 */

import * as THREE from 'three';
import { startLoop, type LoopHandle } from '@/core/loop';
import { Input } from '@/core/input';
import {
  TickTrace,
  TRACE_ACTIONS,
  TRACE_SCRIPTS,
  type TraceBodyTransform,
  type TraceExternalEvent,
  type TraceScriptName,
} from '@/core/trace';
import { TouchControls } from '@/core/touch';
import { PauseOverlay } from '@/core/pause';
import { VerbPrompt, type PromptState } from '@/ui/prompt';
import { createPhysicsWorld, type PhysicsWorld } from '@/physics/world';
import { solidColliderDesc } from '@/physics/solidCollider';
import {
  CharacterController, ROSALIE_TUNING,
  type CharacterState, type Gait, type SpawnPoint,
} from '@/physics/character';
import {
  SpringArmCamera,
  ROSALIE_CAMERA,
  type CameraMode,
  type SpringArmTuning,
} from '@/camera/springArm';
import {
  createTestRoom,
  createTestLighting,
  testRoomSolids,
  testRoomWedges,
  wedgeCorners,
} from '@/world/testroom';
import type { OutDogsDebug } from '@/world/outDogs';
import {
  createSoftlockFixtureRoom,
  softlockFixtureSolids,
  SOFTLOCK_FIXTURE_SPAWN,
} from '@/world/softlockFixture';
import type { CorridorDebug } from '@/world/corridorDirector';
import { BeatFadeDirector } from '@/world/beatFadeDirector';
import { FadeOverlay } from '@/world/fadeOverlay';
import { roomSeamsOf, type ColliderHandle, type RoomCapabilities, type RoomModule, type RoomTick, type RoomView } from '@/game/roomModule';
import { roomModuleFor } from '@/game/roomModules';
import type { HollyDebug, OfficeKestnerDebug, WoolShedDebug, YardDawnDebug, RoomDiagnostics } from '@/game/roomDiagnostics';
import type { BornDebug } from '@/world/bornDirector';
import { framingForAspect } from '@/camera/framing';
import { ChapterCard, chapterMarkForBeat } from '@/ui/chapterCard';
import { MentalMap, type MentalMapSheet } from '@/ui/mentalMap';
import { wallStrokes, type MapSolid } from '@/ui/mapWalls';

/** Seconds the NOTES prompt stays up after something new is written to her map. */
const NOTES_PROMPT_SECONDS = 5;
import type { LatchDebug } from '@/world/latchDirector';
import type { OttoDebug } from '@/world/ottoDirector';
import {
  StealthSystem,
  type ExposureBreakdown,
  type HasLineOfSight,
} from '@/systems/stealth';
import { emissionRadius } from '@/audio/sounds/foley';
import { FootfallStride, heardGait } from '@/systems/footfallStride';
import { passesCameraSweep } from '@/world/solidRoles';
import { BeatSequencer } from '@/story/sequencer';
import { Ledger, WASH_CELL_METRES } from '@/story/ledger';
import { InteractionSystem, type Interactable } from '@/systems/interaction';
import { Bloom, type BloomCensus } from '@/render/bloom';
import { createZoneLighting } from '@/render/lighting';
import { createSky, type Sky } from '@/render/sky';
import { createPostStack, type PostStack } from '@/render/post/stack';
import { applyZone, setExposureReference, setWear } from '@/render/toon';
import { STUDIO_ZONE } from '@/render/studio';
import {
  chromaForBeat,
  wearForBeat,
  type ZonePalette,
} from '@/render/palette';
import { rosalieAsset } from '@/models/rosalie';
import { RosalieRig } from '@/anim/rig';
import { ROSALIE_CLIPS } from '@/anim/clips';
import { AnimationPlayer, ROSALIE_ANIM } from '@/anim/player';
import { BEATS, isRoomId, partForBeat, partForBoot, type RoomId } from '@/story/beats';
import { beatPartModuleFor } from '@/game/beatPartModules';
import { OFFICE_WALL_MAP_ID } from '@/game/rooms/office';
import { HIS_MAP_ZONE, hisMapSheet, tracedRoutes } from '@/game/rooms/officeMap';
import { validateBeatPartModule, type BeatPartModule, type BeatPartTick } from '@/game/beatPartModule';
import { wayGoalFor, type WayContext } from '@/game/wayGoals';
import { Wayfinder, type WayfindingDebug } from '@/systems/wayfinding';
import { runShell } from '@/shell/shell';
import { clearProgress, commitBoundary, effectiveOut, readProgress, validatedOut } from '@/shell/save';
import { gentleMutedSounds } from '@/shell/gentle';
import { PlaythroughTimeline } from '@/shell/timeline';
import { armDevPanel } from '@/shell/devPanel';
import { PerceptionSolver, ROSALIE_PERCEPTION } from '@/anim/perception';
import { neutralPose, type PerceptionTarget } from '@/anim/types';
import { GameAudio, type AudioFrame } from '@/audio/gameAudio';
import {
  showFailure,
  installContextLossHandlers,
  boundedPixelRatio,
} from '@/core/failsafe';

/** Handles the harness reaches for. Kept minimal and explicit. */
export interface GameHandle {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly input: Input;
  /** Golden fixed-tick recorder. Armed only by `?trace=<maxTicks>`. */
  readonly trace: TickTrace;
  readonly physics: PhysicsWorld;
  readonly frameCount: number;
  readonly simTime: number;
  /** Rolling average frame time in ms, over the last ~1 s. */
  readonly frameTimeMs: number;
  /** Last frame's draw calls and triangles. Spec §1 budget: ≤400 draw calls. */
  readonly renderStats: { drawCalls: number; triangles: number };
  /** Raw frame times in ms since the last reset, for percentile measurement. */
  frameSamples(): number[];
  resetFrameSamples(): void;
  /** Rosalie's simulation state, for assertions about movement and gait. */
  readonly character: CharacterState;
  /**
   * Put her back at a known place. The harness uses this between scenarios.
   *
   * `facing` is world radians and defaults to +Z, which is what `reset` has
   * always produced. It is here because an instrument that wants her pointing a
   * particular way otherwise has to *drive* her there — and driving her there
   * moves her, so the sweep ends up comparing framings taken at four different
   * positions. The first opening sweep did exactly that and filmed the underside
   * of her mother four times.
   */
  respawn(x: number, y: number, z: number, facing?: number): void;
  /**
   * Queue a player-only placement for the start of the next fixed tick.
   *
   * It resets Rosalie's controller, velocity and optional facing while preserving
   * the story, companion, dog, queue, bell, audio, stealth and camera state.
   * Unlike `respawn`, this is not a beat reset and does not replace its legacy
   * field-reset semantics; it applies at the next tick boundary, including while
   * simulation is paused and a harness advances one explicit tick.
   */
  teleportPlayer(x: number, y: number, z: number, facing?: number): void;
  /** Stop RAF from advancing the simulation; rendering and the gameplay pause remain independent. */
  pauseSimulation(): void;
  /** Advance this paused runtime by N exact fixed ticks using the current Input snapshot. */
  stepSimulation(steps: number): void;
  /** Return fixed-step advancement to RAF without carrying a wall-clock backlog. */
  resumeSimulation(): void;
  readonly cameraMode: CameraMode;
  /** Live rig values, read from the camera rather than reconstructed by a test. */
  readonly cameraDebug: {
    mode: CameraMode;
    yaw: number;
    rise: number;
    armDistance: number;
    position: { x: number; y: number; z: number };
    fov: number;
    /** 0..1 commitment to spec §7's `focus`. See `SpringArmCamera.focusWeight`. */
    focusWeight: number;
    /**
     * True while an authored framing owns the lens and the spring arm is inert.
     *
     * Here because a frozen camera and a working one are indistinguishable from
     * outside for as long as she stands still, and beats 6 and 7 both shipped
     * frozen. `SpringArmCamera.update()` returns immediately while this is set,
     * so it is the difference between the rig and a still photograph — and
     * `camera-handback.spec.ts` needs to assert the beat took the lens BEFORE it
     * can mean anything by asserting the beat gave it back.
     */
    overridden: boolean;
    /** Her aim point in the presented frame, NDC. `SpringArmCamera.subjectNdc`. */
    subjectNdc: { x: number; y: number };
  };
  /** Live post-stack weather uniforms, exposed only so both weather rooms share one wiring gate. */
  readonly weatherDebug: {
    rainTime: number;
    rainDensity: number;
  };
  /** Live post uniforms that are shared by every graded room. */
  readonly postDebug: {
    /** Read from the shader uniform, never reconstructed from authored inputs. */
    chromaCeiling: number;
  } | null;
  /** The Three constructors verification needs for scene-derived bounds and frusta. */
  readonly three: Pick<typeof THREE, 'Box3' | 'Frustum' | 'Matrix4' | 'Vector3'>;
  /**
   * Alter an authored camera mode only for test and debug evidence. Never use
   * this in play: a settled mode otherwise never re-reads its tuning, so this
   * seam must also restart its blend or a labelled sweep silently captures the
   * old framing instead of the patched one.
   */
  setCameraTuning(
    mode: CameraMode,
    patch: Partial<SpringArmTuning['modes']['pen']>,
  ): void;
  /**
   * Alter the movement-basis yaw only for test and debug evidence. Never use
   * this in play: yaw remains presentation state, and tests use this seam to
   * exercise that real camera-relative path at a named world heading.
   */
  setYaw(radians: number): void;
  /**
   * Freeze the rig on an authored framing (spec §4's cinematic shots).
   *
   * The verification harness needs this too: once a spring arm owns the camera,
   * a screenshot tool that sets `camera.position` directly is overwritten on the
   * very next frame, and every turnaround silently becomes the gameplay view.
   */
  shot(position: { x: number; y: number; z: number }, lookAt: { x: number; y: number; z: number }, fov?: number): void;
  releaseShot(eased?: boolean): void;
  /**
   * What has her attention. Drives head, ears and snout, and nothing else may
   * (D-002). The interaction system now derives a real target from the world
   * and wins whenever it has an opinion (see `snoutTarget` on the render step);
   * this setter is the hand-driven override that has been here since M0a, kept
   * for the harness and for whatever the interaction system leaves unclaimed.
   */
  setPerceptionTarget(target: PerceptionTarget | null): void;
  /**
   * Beat 9's shed, as the harness can see it: the puzzle's own state, and the
   * MEASURED world positions of the things that are supposed to follow it.
   *
   * The measured half is the point. A gate that reads `bar: 'lifted'` back out
   * of the machine that set it has confirmed nothing — `docs/traps.md` records
   * the beat 5 gate that asserted where a lens was and never what it could see.
   * `barY` and `muzzleY` come off the scene graph, so the assertion is that the
   * bar actually moved and that Holly's nose actually got to it.
   */
  readonly woolShedDebug: WoolShedDebug | null;
  /** Beat 11: Kestner's presentation clock and whether the glasses are off. */
  readonly officeDebug: OfficeKestnerDebug | null;
  /** Beat 12: the dawn yard's marks-as-state (lights, ramp motor) and its live pivots. */
  readonly yardDawnDebug: YardDawnDebug | null;
  /** Generic room diagnostic seam for module-owned beats. */
  readonly roomDebug: RoomDiagnostics | null;
  /** Live stealth state for module-room gates. Null means the room constructed no system. */
  readonly stealthDebug: { watchers: number; alarmLevel: number } | null;
  readonly twelveOutDebug: {
    /** Monotonic evidence that the real dog-capture operation ran. */
    captures: number;
    holly: { x: number; z: number; yaw: number; phase: string; moving: boolean; travelled: number };
    hollyY: number; call: 'lie-up' | 'with-her'; travelling: boolean;
    bell: { events: number; lastThresholdDistance: number; lastX: number; lastZ: number; radius: number } | null;
    dog: { x: number; z: number; facing: number; watcherY: number; suspicion: number; state: string; phase: string; targetX: number; targetZ: number; distanceFromPost: number; investigations: number; drives: number; turnBack: 'out' | 'in' | null };
    alarmLevel: number; exposureAt: (x: number, z: number) => number;
    /**
     * Stage 4's queue and the bodies it is drawing. Null on any boot without
     * a huddle, so a spec that reads it is asserting the beat and not the room.
     *
     * `drawnAt` and `visible` are read back off the INSTANCE MATRICES, not
     * recomputed from `members[i].distance`. This beat has twice shipped a debug
     * value built from the caller's own expression, which agrees with itself
     * whatever the caller stored (`docs/traps.md`); here that failure would be a
     * herd gate that passes against a `NightHuddle` drawing nothing at all.
     */
    herd: {
      clock: number | null; out: number; scattered: number; queued: number; throughs: number;
      headDistance: number; drawn: boolean; ended: boolean; lastCue: string | null;
      serving: number | null;
      /**
       * The stall, and `stalls` is the instrument rather than `stalled`.
       *
       * A stall is a state a gate can sample either side of — she rolls away and
       * comes back — and `docs/traps.md` has a paragraph in capitals about a
       * gate that samples a transient being a race that happens to win. The
       * counter is monotonic and cannot be missed.
       */
      stalled: boolean; stallReason: 'range' | 'offset' | 'blocked' | null; stalls: number;
      /** The exact dog-derived barrier handed to the latest fixed queue step. */
      blocked: boolean; barrier: number | null;
      /**
       * Presentation's half of the voice, and it is here because nothing else
       * can see it. The CALLS are counted by the audio engine itself —
       * `audioDebug.oneShotStartsById['herd-stall']`, read off the thing that
       * plays them — but the bed is one long loop with a gain ramp, and without
       * this a gate could not tell a bed that is playing from one that was
       * never started.
       */
      bedGain: number; bedPeak: number; callsPending: number;
      /**
       * Whether the two loops are ACTUALLY running, off the handles.
       *
       * `bedGain` is a ramp target and says nothing about whether anything is
       * playing — a stopped-and-restarted bed passes through the same numbers a
       * live one does. These are `handle.active`, which is the only thing that
       * can see the close's latch working.
       */
      bedPlaying: boolean; pantPlaying: boolean;
      states: readonly string[];
      distances: readonly number[];
      /** Where instance `i` actually is this frame, off its matrix. */
      drawnAt: (index: number) => { x: number; y: number; z: number };
      /** How many of the twelve are still bodies in the field, off the matrices. */
      visible: number;
      /** The route the herd walks, so a gate derives its expectations from the room. */
      routeLength: number;
      /** Where SHE is on it, which is the queue's whole input. */
      lead: { distance: number; offset: number };
    } | null;
  } | null;
  /**
   * The hedgerow's time of day, as its room module chose it (`HedgerowDebug`).
   *
   * **It is not gated on beat 10, and that is the whole point.** The first
   * version was `beat10Debug` and returned null for every other boot, which made
   * the only available assertion "the night boot has night values" — a level, and
   * D-100 says a level is also what an unchanged room reports. Exposed for any
   * hedgerow boot, a spec can hold beat 7's field and beat 10's beside each other
   * and compare, which is the delta this stage exists to prove.
   *
   * `backgroundMeanLuminance` is the baked sky's own pixels, averaged. The value
   * it replaced was `scene.background === room.sky.background`, which is true on
   * a day boot too — the room always bakes a sky and this file always assigns it,
   * so the flag could not go red for the failure it was named after (D-160: the
   * doorway that rendered a daylight slab at midnight).
   */
  readonly hedgerowDebug: {
    night: boolean;
    zoneId: string;
    keyIntensity: number;
    exposureReference: number;
    backgroundMeanLuminance: number;
  } | null;
  readonly hollyDebug: HollyDebug | null;
  readonly animationDebug: {
    from: string; to: string; weight: number; phase: number;
    /** Total locomotion cycles, unwrapped. See `AnimationPlayer.debug`. */
    cycles: number;
  };
  /**
   * The mix, as the harness can see it. There is no output device in a headless
   * browser, so a dropped voice and a played one sound identical — these counters
   * are the only way a test can assert that her footfalls actually fire.
   */
  readonly audioDebug: {
    started: boolean;
    muted: boolean;
    contextState: string | undefined;
    activeVoices: number;
    cachedSounds: number;
    oneShotsStarted: number;
    /** Per-sound one-shot starts; the total alone cannot attribute a stroke. */
    oneShotStartsById: Record<string, number>;
    lastError: string | undefined;
    /** Sounds Gentle Mode is refusing to start. Empty with the mode off. */
    suppressed: readonly string[];
    /** Room machinery loops still contributing to the mix, never breath or foley. */
    ambience: readonly string[];
  };
  readonly corridorDebug: CorridorDebug | null;
  /**
   * Beat 5, as numbers. Null unless `?beat=5` is running.
   *
   * `cycle` is the plant's phase and it is here for one reason: the beat's claim
   * is that the machine never reacts to her, and the only way to state that as a
   * gate is to sample the phase across a run and show it is a metronome. The
   * rest is the ride — a stage that holds a clock proves nothing (D-090), so the
   * spec asserts on where the bin actually got to.
   */
  readonly cullDebug: {
    cycle: number;
    lift: number;
    binZ: number;
    collectorZ: number;
    /** True only from the RIGHTING onward — see `CullDirector.enterBin`. */
    carrying: boolean;
    ride: string;
    /** 0 upright, 1 flat on its side. The topple, as the simulation holds it. */
    tip: number;
    /** True once she is in the bin, whether or not anybody has picked it up. */
    inside: boolean;
    fade: number;
  } | null;
  /**
   * Beat 6, as numbers. Null unless `?beat=6` is running.
   *
   * Four of these exist because a claim this beat makes is otherwise only
   * checkable by looking at a photograph. `flash` and `tell` are the storm's
   * envelope; `coneHalfAngle`, `range` and `facing` are Kestner's LIVE watcher,
   * so gate 6 can assert that his cone widens on the tell and narrows after the
   * decay **as a delta** rather than sampling a level and calling it a rule.
   *
   * `strikes` is the determinism seam: the sim time of every tell since boot.
   * D-045 requires the simulation to replay, and two runs of the same seed
   * producing the same strike times to the frame is the only way to say so.
   */
  readonly yardDebug: {
    flash: number;
    tell: boolean;
    alert: boolean;
    coneHalfAngle: number;
    range: number;
    facing: number;
    captureLive: boolean;
    carrying: boolean;
    ended: boolean;
    fade: number;
    sheltered: boolean;
    strikes: readonly number[];
  } | null;
  /**
   * Beat 7's whole state, or null unless beat 7 is running.
   *
   * Every field here exists because a gate reads it instead of trying to infer a
   * state from pixels, and three of them are the beat's DELTAS rather than levels
   * (D-100):
   *
   * - **`bloom`** is the raw `t`, and `bloomCensus` is the three eased curves plus
   *   what each one actually wrote. Gate 1 asserts that `chromaCeiling` has NOT
   *   moved at t = 0.34 and HAS by t = 0.36 — the "chroma arrives last" staging,
   *   which is the only thing separating this bloom from a saturation slider.
   * - **`wear`** is read back off the uniform, not off a field the director also
   *   writes, so gate 2's before/after difference cannot pass on a `setWear()`
   *   call that never reached the material. That distinction is exactly how this
   *   channel sat unwired for six beats (D-116).
   * - **`dogs.shelteredSamples`** against `dogs.samples` is rule 1's own number:
   *   *hiding leaves evidence*. It is non-zero before she has deposited anything
   *   at all, because the men's trail along the shed wall is seeded at boot — and
   *   that is also the check that the wall's shelter volume actually contains the
   *   line the dogs are given to work.
   *
   * `captures` is how the no-input gate says the two backstops both fired.
   */
  readonly outDebug: {
    bloom: number;
    wear: number;
    boardsBroken: boolean;
    boardStrains: number;
    /** Pushes, roots and slow rolls the timber answered (D-209). */
    boardAnswers: number;
    captures: number;
    driving: boolean;
    cut: boolean;
    looksBack: boolean;
    headingOut: boolean;
    ended: boolean;
    dogs: OutDogsDebug;
    bloomCensus: BloomCensus;
    fade: number;
    sheltered: boolean;
    cameraHeld: boolean;
  } | null;
  /**
   * `exposureAt` in the yard, through the SAME decorated grid the stealth system
   * holds. Null unless beat 6 is running.
   *
   * The beat's flash gate is a delta on this: one sample between strikes, one at
   * peak, at a fixed point in open yard — and the same pair under the trailer,
   * where the difference must be *smaller*, because `exposureAt` is
   * `lightLevel × (1 − concealment)` and cover survives the storm.
   *
   * It routes through the live grid rather than re-baking one, and that is the
   * whole reason it is a handle method instead of a spec-side calculation: a
   * gate that built its own grid would prove that a decorator *can* work, not
   * that the game is using one.
   */
  yardExposureAt(x: number, z: number): ExposureBreakdown | null;
  /**
   * Where the current beat has got to, or null in a build with no beat running.
   *
   * Deliberately the sequencer's own debug view rather than a hand-assembled
   * one: a second description of the same state is a second thing that can be
   * wrong, and this one is already asserted against by `story.spec.ts`.
   */
  /**
   * D-209's stall clock and looks, for the stage in progress. Null before the
   * first tick. `looks` and `calls` count since boot, so a gate can assert a
   * DELTA across an idle stretch rather than a level.
   */
  readonly wayfindingDebug: WayfindingDebug | null;
  readonly storyDebug: {
    stageId: string | null;
    stageIndex: number;
    stageTime: number;
    beatTime: number;
    cuesEmitted: number;
    finished: boolean;
  } | null;
  /**
   * Beat 8 part 2's world facts. Null in every other beat.
   *
   * These are read from the director rather than reconstructed from the scene
   * for one reason each: `freed` counts birds whose only trace in the graph is a
   * per-instance matrix, and `torchGone` is a phase that ends by making its own
   * figure invisible — reconstructing either from a traverse would be asserting
   * on the absence of something, which is how a gate ends up unable to go red.
   */
  readonly blackoutDebug: {
    /** Hens out of a crate and on the floor. Never shown to the player (spec §9). */
    freed: number;
    flockSettled: boolean;
    torchGone: boolean;
    /** The sweep passed over her. Not a fail state — see `BlackoutTorch.noticed`. */
    noticed: boolean;
  } | null;
  /**
   * What the beat has done to the SCENE. Null when no beat is running.
   *
   * `lampLevel` exists because the taking is told entirely by a light going
   * down and coming back up, and there is no way to assert on a photograph.
   */
  readonly beatDebug: BornDebug | null;
  /**
   * Beat 3's state, as numbers. Null unless `?beat=3` is running.
   *
   * A third of these rather than one shared shape, for the reason the second one
   * gives: the three beats do not have the same state and an interface wide
   * enough for all of them would describe none of them.
   */
  readonly ottoDebug: OttoDebug | null;
  /**
   * Beat 2's simulation, as numbers. Null unless `?beat=2` is running.
   *
   * Separate from `beatDebug` rather than folded into it, because the two beats
   * measure different things and a union of both would be a shape where half the
   * fields are always null — which is a shape a test cannot assert against
   * without first working out which beat it is in.
   *
   * Everything here is FIXED-STEP state. That is what makes it worth exposing:
   * the whole risk in this beat is a worker or a gate drifting onto the render
   * step (D-044), and a harness that can read his position tick by tick is how
   * that would be caught.
   */
  readonly latchDebug: LatchDebug | null;
  /** What her snout is on, and how far through a hold she is. Test seam. */
  readonly interactionDebug: {
    actId: string | null;
    observeId: string | null;
    progress: number;
  } | null;
  /** The saved map data, exposed as counts rather than renderer-shaped state. */
  ledgerDebug(): { observed: string[]; washCells: number; zones: string[] };
  /** The current contextual verb prompt. Test seam. */
  readonly promptDebug: PromptState;
  /**
   * Jump the beat to a named stage. **Test and debug only.**
   *
   * The beat is three minutes long and most of that is deliberately slow; a spec
   * that wants to photograph the two seconds of shadow in the middle of it
   * cannot afford to play the whole thing four times. Runs every intervening
   * stage's enter and exit, so the world arrives in the state it would have been
   * in — see `BeatSequencer.skipTo`.
   */
  skipToStage(stageId: string): void;
  /** Commit hash and build time of the running bundle. */
  readonly buildId: string;
}

declare global {
  interface Window {
    __rosalie?: GameHandle;
    /**
     * Test seam: set to `false` before boot to keep a restored GPU context from
     * reloading the page. See `CONTEXT_LOSS_KEY` below for why this exists.
     * Undefined everywhere except under the harness, so players always get the
     * recovery behaviour.
     */
    __rosalieAutoReload?: boolean;
  }
  /** Injected by vite.config.ts. Commit hash and build time. */
  const __BUILD_ID__: string;
}

/**
 * Reload at most twice per session, then stop and leave the failure visible.
 *
 * A GPU that is genuinely unwell — thermal throttling, a driver flapping, a
 * laptop switching between graphics adapters — can lose and restore a context
 * repeatedly. Reloading unconditionally on every restore is an unbounded reload
 * loop that a player cannot escape except by closing the tab, and it is exactly
 * the failure the service-worker listener already guards against.
 *
 * `sessionStorage` rather than a module variable, because the whole point is to
 * survive the reload it is counting.
 */
const RELOAD_KEY = 'rosalie:auto-reloads';
const MAX_AUTO_RELOADS = 2;

/**
 * A breadcrumb counting context losses, surviving the reload that follows one.
 *
 * A lost context is invisible after the fact: the reload wipes the console, the
 * page comes back looking healthy, and the only trace is whatever the thing
 * watching happened to be in the middle of. In the test suite that produced a
 * bare "Execution context was destroyed" inside `advanceFrames`, which reads as
 * a movement regression and is really the GPU being reclaimed — a full suite run
 * failed twice this way while every one of those tests passed in isolation.
 *
 * Diagnosing that cost more than the counter does, so the counter stays.
 */
const CONTEXT_LOSS_KEY = 'rosalie:context-losses';

function readCounter(key: string): number {
  let value = 0;
  try {
    value = Number(window.sessionStorage.getItem(key) ?? '0');
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Treat that as
    // "nothing recorded yet" rather than failing inside a failure handler.
  }
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function bumpCounter(key: string): void {
  try {
    window.sessionStorage.setItem(key, String(readCounter(key) + 1));
  } catch {
    // Never let bookkeeping throw out of a recovery path.
  }
}

function reloadOnce(): void {
  // The harness suppresses the navigation so a reclaimed context fails the test
  // it happened in, by name, instead of destroying that test's execution context
  // and blaming whatever assertion was next. The player-facing path is unchanged
  // and is what `failsafe.spec.ts` exercises.
  if (window.__rosalieAutoReload === false) {
    showFailure(
      'The graphics context was lost.',
      'Automatic reloading is disabled in this build.',
      false,
    );
    return;
  }

  const count = readCounter(RELOAD_KEY);

  if (count >= MAX_AUTO_RELOADS) {
    showFailure(
      'The graphics context keeps failing.',
      'Rosalie stopped reloading automatically after ' +
        `${MAX_AUTO_RELOADS} attempts, so you are not stuck in a loop. ` +
        'This usually points at the GPU driver rather than at the game.',
    );
    return;
  }

  bumpCounter(RELOAD_KEY);
  window.location.reload();
}

/** The `kind` values the perception solver knows. Anything else throws. */
const PERCEPTION_KINDS: readonly PerceptionTarget['kind'][] = [
  'focus',
  'sound',
  'scent',
  'interactable',
];

/** Where she starts in the grey-box room: open floor, ramp and steps both reachable. */
const SPAWN = { x: 0, y: 0.2, z: 0 };


/** Mute survives a reload, because having to re-silence a game is infuriating. */
const MUTE_KEY = 'rosalie:muted';

/** The listener's up vector never changes: the camera does not roll. */
const LISTENER_UP = { x: 0, y: 1, z: 0 };

/**
 * Height of the axis she rolls about when upright, metres.
 *
 * Describes her TUCKED profile, not her standing one — she is a different shape
 * while rolling. The tuck squeezes her along her length and lets that volume out
 * upward and sideways, so mid-roll she is shorter front-to-back and slightly
 * taller than the 0.55 x 0.32 m she measures standing still. Using the standing
 * figures left her dipping ~33 mm at the diagonals.
 *
 * Whatever this value is, the upright case stays exact: the roll node is raised
 * by it and the mesh pushed down by the same amount, so they cancel at zero
 * spin. Rolling about her ORIGIN instead — which is at her feet — is what drove
 * her whole body through the floor in the first place.
 */
const ROLL_PIVOT_Y = 0.19;

/**
 * How far she reaches from that axis along her length while TUCKED.
 *
 * She is not a ball. Tumbling a body this shape about a fixed centre sweeps a
 * radius far larger than her height, so a quarter turn used to bury 11 cm of
 * her in the floor — which is exactly what "the rolling gets into the ground a
 * bit" was.
 */
const ROLL_REACH_Z = 0.25;

/**
 * The distance she covers in one revolution while rolling, metres.
 *
 * This is the PERIMETER of her rolling cross-section, not the circumference of
 * a circle through her height. Treating her as a 0.30 m ball made her spin
 * about 45 % faster than the ground was going past — it read as the animation
 * outrunning the movement, which is exactly what it was.
 *
 * She rolls on an oval: 0.55 m along her length, 0.32 m through her height. Any
 * point on that outline travels the full perimeter per revolution, so that is
 * the distance one turn buys. Ramanujan's approximation, accurate to well
 * inside a millimetre here, and derived from the same two constants the roll
 * height uses so the three cannot drift apart.
 */
const ROLL_CIRCUMFERENCE = (() => {
  const a = ROLL_REACH_Z;
  const b = ROLL_PIVOT_Y;
  return Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
})();

/**
 * The height her rotation axis must sit at, for her to just touch the ground at
 * a given roll angle.
 *
 * Superellipse support, exponent 4, i.e. `(|a·sin|^m + |b·cos|^m)^(1/m)` with
 * m = 4/3. Two simpler forms were tried and both were wrong at one end:
 *
 *   - an ELLIPSE cuts ~6 cm inside the corners, so she clipped the floor at the
 *     diagonals;
 *   - a BOX support with a flat 0.94 discount fixed the diagonals but scaled the
 *     0.275 m long-axis endpoint down to 0.2585, so she dipped ~16 mm at the
 *     quarter turn — caught by review, not by the test, whose tolerance was
 *     loose enough to admit it.
 *
 * The superellipse is EXACT at both axis endpoints and sits sensibly between
 * ellipse and box at 45 degrees, which is what a rounded body actually does.
 *
 * The consequence is that she RIDES UP as she comes onto her long axis and
 * settles back twice per revolution. That is not a workaround: it is what any
 * non-spherical thing does when it rolls. An egg does exactly this.
 */
function rollAxisHeight(spin: number): number {
  const s = Math.abs(Math.sin(spin));
  const c = Math.abs(Math.cos(spin));
  // The 0.94 corner discount applies ONLY to the long-axis term. Applied to the
  // whole expression it also scales the upright case, which quietly sank her a
  // centimetre into the floor whenever she was NOT rolling — a fix for the roll
  // that broke standing still.
  return ROLL_PIVOT_Y * c + ROLL_REACH_Z * 0.94 * s;
}

/**
 * The camera sweep ignores anything outside membership bit 0.
 *
 * ── Why this exists: the pen rail, and a measured game-feel regression ──────
 *
 * Rapier interaction groups are `(memberships << 16) | filter`, and a query
 * sees a collider only if `query.filter & collider.memberships` is non-zero.
 * Everything in this game keeps Rapier's default `0xFFFFFFFF`, so bit 0 is
 * set on all of it; the pen rail is created in bit 1 alone (`CAMERA_PASSES`
 * below) and therefore drops out of this one query and out of nothing else.
 *
 * It is not a nicety. Making the drawn rail solid (D-056) put a 1.03 m steel
 * frame along both sides of a 1.4 m walkway, and the follow lens rides at
 * 0.67–0.72 m — squarely inside it. Measured before adding this filter: with
 * her at x = 0.55 the arm collapsed from **1.370 m to 0.550**, i.e. the camera
 * jammed against the back of her head every time she walked near a kerb. It
 * also showed up as **10 fewer draw calls** in `check:budget`, which is how it
 * was noticed at all — the counting gate caught a camera bug, which is not
 * what it was built for and is a good argument for it.
 *
 * The rail stays solid to everything that matters: the character controller
 * collides with it, and the scramble's ledge and overhead raycasts still see
 * it, which is what actually refuses a climb onto the kerb. A camera that
 * passes through a guard rail is correct; a piglet that does is the bug.
 */
const SWEEP_GROUPS = 0xffff_0001;

/**
 * Membership bit 1 alone: solid to everything, invisible to the camera sweep.
 *
 * The filter half stays 0xFFFF so the rail still interacts with every other
 * collider — it is the MEMBERSHIP that takes it out of `SWEEP_GROUPS`.
 */
const CAMERA_PASSES = 0x0002_ffff;


/**
 * How long the handover will wait for the screen to go black before giving up
 * and going anyway, in seconds.
 *
 * Every beat today ends with five seconds of fade whose target is 1, so the
 * screen is black long before the `end` cue and this timeout never fires. It is
 * here so that a beat which someday ends *without* fading hands over on a cut
 * instead of doing the exact thing this mechanism exists to fix — sitting on a
 * finished beat forever.
 */
const CHAIN_MAX_WAIT = 2;

/**
 * The mean luminance of a baked sky texture, 0..255, or −1 when there is none.
 *
 * **This exists because "is there a sky" is not the question.** `createZoneLighting`
 * hands back `zone.fog` and `createSky` bakes a `DataTexture`; both produce a
 * non-null `scene.background` in every room in the game, so any assertion shaped
 * like *the background is set* or *the background is the room's own bake* is green
 * against the exact failure D-160 records — a daylight sky rendered at midnight.
 * The pixels are the only thing that can tell those apart, and a `DataTexture`
 * carries them, so a spec can compare beat 7's field with beat 10's without a
 * screenshot.
 *
 * It reads `image.data` and returns −1 rather than throwing for anything else: a
 * compressed or render-target background is not something this game builds, and a
 * debug getter that throws takes the whole handle down with it.
 */
function bakedSkyMeanLuminance(texture: THREE.Texture | null): number {
  const data: unknown = (texture?.image as { data?: unknown } | undefined)?.data;
  if (!(data instanceof Uint8Array) || data.length < 4) return -1;
  let total = 0;
  for (let i = 0; i + 2 < data.length; i += 4) {
    total += 0.2126 * (data[i] ?? 0) + 0.7152 * (data[i + 1] ?? 0) + 0.0722 * (data[i + 2] ?? 0);
  }
  return total / (data.length / 4);
}

async function boot(): Promise<void> {
  // The service-worker reload listener is NOT here. It lives inline in the head
  // of index.html, because by the time this module body runs, Three and Rapier
  // have already loaded and a new worker could have claimed the page during
  // that window with nothing listening. See the comment there.
  //
  // The build stamp goes in the TITLE, not the console. Spec §0 requires zero
  // console output at runtime and D-005 says our own warnings get fixed rather
  // than added to the harness's exception list. A title is also strictly more
  // useful to a reviewer: visible without opening devtools, and visible on a
  // phone, where devtools is not really an option.
  document.title = `ROSALIE — ${__BUILD_ID__}`;

  // ── The shell runs before anything is built, and only for a person ─────────
  //
  // `?beat=` absent means somebody opened the game; `?beat=N` present means
  // something asked for a specific beat — a spec, the chain hand-over, the
  // hidden jump, or Continue. That rule is what keeps every existing test
  // booting straight into the world: `tests/e2e/harness.ts` sets `beat` on
  // EVERY boot, explicitly and on purpose, and says so.
  //
  // It is here, above the canvas and the renderer, because the shell is DOM and
  // ends in a navigation. Building a WebGL context, a physics world and a room
  // for a document that is about to be replaced would cost a player the whole
  // load twice, on the one screen where nothing is moving yet.
  const params = new URLSearchParams(window.location.search);
  if (params.get('beat') === null) {
    await runShell(params);
    return;
  }

  const canvas = document.querySelector<HTMLCanvasElement>('#game');
  if (!canvas) throw new Error('#game canvas not found');

  /** Declared early so the context-loss handler can stop it before it exists. */
  let loop: LoopHandle | null = null;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    // Ask for the discrete adapter where there is a choice. Free on this box —
    // a Vega 3 is the only GPU there is — and the right default for a laptop
    // with switchable graphics, where the browser otherwise picks the
    // power-saving one and every frame budget we measured here becomes a lie.
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(
    boundedPixelRatio(
      canvas.clientWidth || window.innerWidth,
      canvas.clientHeight || window.innerHeight,
      window.devicePixelRatio,
    ),
  );
  renderer.shadowMap.enabled = true;
  // Frame stats are accumulated by hand, because a frame is no longer one
  // render call.
  //
  // `renderer.info` resets itself at the top of every `render()`, so once the
  // post stack landed — normal prepass, beauty pass, composite — the numbers the
  // harness reads described only the LAST pass: a single fullscreen triangle.
  // `renderStats` cheerfully reported **1 draw call and 1 triangle for a dressed
  // farrowing hall**, and the perf budget would have passed for ever. Resetting
  // once per frame in the render step is what makes the number mean the frame
  // again, and it now includes the prepass, which is honest.
  renderer.info.autoReset = false;
  // PCFSoftShadowMap is deprecated as of three 0.185; PCFShadowMap is the default.
  // Shadow quality is an M0b art decision anyway — do not tune it here.
  renderer.shadowMap.type = THREE.PCFShadowMap;

  /**
   * Constructed HERE, before the context-loss handler that references it.
   *
   * The handler is deliberately installed as early as possible (see below), and
   * it now disposes the audio. A `const` declared further down would be in its
   * temporal dead zone at that point, so a context loss during boot would throw
   * a ReferenceError *inside the failure handler* — the exact shape of bug the
   * last hardening round was about. Constructing it first removes the ordering
   * hazard rather than guarding against it.
   *
   * The constructor is silent and creates no `AudioContext`; only `start()`
   * does, from a user gesture.
   */
  const audio = new GameAudio();
  try {
    audio.setMuted(window.localStorage.getItem(MUTE_KEY) === '1');
  } catch {
    // Storage being unavailable must not stop the game booting.
  }

  // Gentle Mode's mute half, applied once at construction because the mode is
  // chosen at the content notice — before any beat boots — and cannot change
  // while a beat is running. The list is authored in `src/shell/gentle.ts`; the
  // engine is only told what to refuse (D-018 keeps content knowledge out of it).
  audio.setSuppressedSounds(gentleMutedSounds());

  // Attached HERE, not after the scene is built.
  //
  // Boot awaits the Rapier WASM init and then constructs every asset, which is
  // several hundred milliseconds — and precisely the window in which GPU memory
  // pressure and driver resets are most likely. A loss during it used to be
  // dispatched to nothing at all: `preventDefault` never ran, so restoration
  // became impossible, and boot carried on building a scene and starting a loop
  // against a context that was already dead, with no overlay ever shown.
  let contextLost = false;

  /**
   * Declared HERE, above the context-loss handler that disposes it, for exactly
   * the reason spelled out over `audio` a few lines up — and it was got wrong
   * anyway the first time.
   *
   * The handler disposes the post stack, and `post` was originally declared
   * inside the room-selection branch, several hundred milliseconds of `await`
   * later. A context loss in that window — which is *the* window this handler
   * exists for — would have thrown a ReferenceError from its temporal dead zone,
   * **inside the failure handler**, killing the recovery path outright. Found on
   * independent review.
   *
   * Null in the grey-box room: it is a measuring instrument (D-023) and renders
   * raw, because ink outlines and a saturation clamp over a room whose whole job
   * is to make motion problems obvious would be working against it.
   */
  let post: PostStack | null = null;
  // A baked exterior sky (the yard's, the dawn yard's, the hedgerow's), held so its texture can
  // be disposed with the rest of the room. It is a resource owner, not a
  // statement about which room has weather.
  let roomSky: Sky | null = null;
  /**
   * Whether this room owns an advancing rain layer.
   *
   * Sky ownership and weather are different facts: the hedgerow used to bake
   * its own sky inside `createHedgerow`, so `roomSky` was null there even though
   * its post stack had rain. Using that sky handle as the weather predicate froze
   * beat 7's rain for three sessions. Only a module room that declares
   * `RoomEnvironment.rain` may set this flag.
   */
  let weatherActive = false;
  /**
   * A module room's sky term and the intensity it sits at when nothing is
   * happening. Held for every module room; only beat 6's flash writes it.
   *
   * D-107: `storm.flash` arrives in three places and this is the one that makes
   * surfaces climb the toon ramp rather than merely making the image brighter.
   * It is deliberately not a proxy for whether rain is active: the hedgerow
   * rains and has no lightning.
   */
  let roomHemi: THREE.HemisphereLight | null = null;
  let roomHemiBase = 0;
  let stealth: StealthSystem | null = null;
  let callPromptTaught = false;
  /**
   * How many observations have been written to her notes this boot, and how
   * many the prompt has already announced. Sim writes the first; the render
   * step reads it and offers NOTES for a few seconds after each new one (D-237),
   * because a sheet nobody knows exists is not a mechanic.
   */
  let notesRecorded = 0;
  let notesAnnounced = 0;
  let notesPromptSeconds = 0;
  /** Bound once: the bell drain runs on frames that ring, and a `bind` there is per-frame garbage. */
  const playOneShot = audio.engine.playOneShot.bind(audio.engine);

  let disposeVerbPrompt: (() => void) | null = null;
  let disposeMentalMap: (() => void) | null = null;
  /**
   * Whichever beat's director is running, so context loss can release it.
   *
   * A forward-declared hook rather than a direct reference, for the same reason
   * `post` is a mutable binding: this handler is installed long before either
   * director exists, which is the point — a context lost during boot must still
   * be handled.
   *
   * Both directors put a full-screen black `<div>` on the page and neither was
   * ever being disposed. On the branch that does NOT reload — the harness, or any
   * future in-place recovery — that overlay outlives the game it was fading, and
   * a black rectangle over a failure message is a worse failure message. Beat 1
   * had this too; found on independent review of beat 2.
   */
  let disposeBeat: (() => void) | null = null;
  /**
   * What the ROOM owns, as opposed to what the beat owns.
   *
   * Separate from `disposeBeat` because the room outlives any one part and
   * owns things no part should be able to drop by reassigning a disposer
   * (beat 10's night dog's fade overlay and pant loop were the case that made
   * this two variables). Two variables and two calls is the cheapest way to
   * make that impossible rather than merely unlikely.
   */
  let disposeRoom: (() => void) | null = null;
  /**
   * Takes the hidden panel's global listeners back off `window`.
   *
   * A `let` assigned later rather than a `const` at the point of arming, for the
   * same reason `disposeBeat` is one: the context-loss handler below is attached
   * before any of this exists, and a closure reaching a `const` that has not
   * been initialised yet throws instead of being null.
   */
  let disarmDevPanel: (() => void) | null = null;

  installContextLossHandlers(canvas, {
    onLost: () => {
      contextLost = true;
      bumpCounter(CONTEXT_LOSS_KEY);
      // The audio has nothing to do with the GPU, but it has no reason to keep
      // playing to a dead frame either — and on the branch that does NOT reload
      // (the harness, or any future in-place recovery) nothing else would ever
      // release its voices or its visibility listener. Raised on review.
      audio.dispose();
      // Its render targets belong to the context that just went away. Releasing
      // them is the difference between recovering and leaking two full-screen
      // buffers per loss on a GPU that is already unwell.
      post?.dispose();
      post = null;
      // A baked sky owns a texture and it belongs to the context that just
      // went away, exactly as the post stack's targets do. Null in rooms with a
      // flat background, where this is a no-op.
      roomSky?.dispose();
      roomSky = null;
      disposeVerbPrompt?.();
      disposeMentalMap?.();
      disarmDevPanel?.();
      disposeBeat?.();
      disposeRoom?.();
      for (const fade of moduleFades) fade.dispose();
      // May be null if this fires before the loop exists — that is the whole
      // reason the handler is attached this early.
      loop?.stop();
    },
    onRestored: () => {
      reloadOnce();
    },
  });

  /**
   * Which room boots.
   *
   * The game ships the farrowing hall (spec §7 beats 1–2). The grey-box test
   * room survives behind `?room=testroom` because it is a MEASURING INSTRUMENT,
   * not a scene: its step ladder, ramp and crawl tunnel are what
   * `movement.spec.ts` asserts against, and those assertions are the reason
   * anyone can trust a change to the controller. D-004 ends the placeholder
   * exemption for *shipped scenes*, and a fixture reachable only by a query
   * parameter the game never sets is not one.
   *
   * Deleting it would not simplify anything; it would delete the evidence.
   */
  /**
   * Whether Act 1 beat 1 runs, or the hall is simply a room she is standing in.
   *
   * `?beat=0` is what every spec that is about the CONTROLLER rather than about
   * the story boots with, and it is not a debug convenience — it is a
   * correctness requirement. The beat spawns her inside a farrowing pen, holds
   * the camera for the first nine seconds, and moves a pair of boots down the
   * walkway on a timer. A movement test measuring her stopping distance under
   * all that is measuring the beat, and a perf test measuring the frame budget
   * with a scripted mover in it is measuring the wrong scene.
   *
   * **It is a table now, and beat 3 is why.** Session 14 left this a two-way
   * branch on the grounds that a table built from two entries is a guess about
   * the third; beat 3 is the third, and it is also the first beat that is not in
   * the farrowing hall — so the thing a beat selects is no longer just a
   * director, it is a *room*. Two entries could not have told us that. Three do.
   */
  const requestedBeat = params.get('beat');
  const beatNumber = requestedBeat === null ? 1 : Number(requestedBeat);
  const beat = BEATS[beatNumber];
  if (!Number.isInteger(beatNumber) || !beat) {
    throw new Error(
      `unknown beat "${requestedBeat ?? ''}" — known beats are ${Object.keys(BEATS).join(', ')}`,
    );
  }
  const requestedPart = params.get('part');
  const partNumber = requestedPart === null ? 1 : Number(requestedPart);
  const part = partForBoot(beatNumber, partNumber, params.get('room'));
  if (part === null) {
    throw new Error(
      `unknown part "${requestedPart ?? ''}" for beat ${beatNumber} — known parts are 1…${beat.parts.length}`,
    );
  }
  const beatWanted = beatNumber !== 0;

  /**
   * Whether a finished beat hands over to the next one. **On by default: this is
   * how Act 1 is played, and `?chain=0` is the exception.**
   *
   * The exception exists for the specs. `born.spec.ts` plays beat 1 to its end
   * without pressing a key — that is the point of it — and a chain would
   * navigate the page out from under the assertions in the last five seconds of
   * a three-minute test. `tests/e2e/harness.ts` therefore sets this explicitly
   * on every boot, the same way and for the same reason it sets `beat`.
   */
  const chainWanted = params.get('chain') !== '0';
  /** The beat that follows this one, once the current one has finished. */
  const chainNextPart = chainWanted ? partForBeat(beat, partNumber + 1) : null;
  const chainNext = chainWanted && chainNextPart === null ? beat.next : undefined;

  /**
   * Which room boots: **the beat's room, unless `?room=` names one.**
   *
   * That is the inversion beat 3 forced. Until now the room was the constant and
   * the beat was the variable, because there was only ever one room; now the beat
   * is the thing the player asks for and the room follows from it.
   *
   * `?room=` still wins, and it is how the two measuring instruments are reached.
   * A room that does not match the requested beat does **not** clear the beat —
   * it simply means `beatPartModuleFor` finds no part (the registry keys on
   * beat, part AND room), which is what every existing spec booting
   * `?room=testroom` relies on.
   */
  const requestedRoom = params.get('room');
  const roomId: RoomId = isRoomId(requestedRoom) ? requestedRoom : part.room;
  /**
   * Which sheet her notes go on, and it is the ROOM — not the palette zone.
   *
   * **This was the palette zone for one commit and it was wrong**, found on
   * independent review. `HEDGEROW_ZONE_ID` is `'yard'`
   * (`src/world/hedgerow.ts`), because beat 7's field opens in the yard's
   * palette and blooms out of it — which is correct for a *look* and disastrous
   * for a *sheet*. Beat 6's yard and beat 7's field are two rooms built at two
   * origins with two coordinate systems, and keying the ledger on the palette
   * merged them: the player's yard notes would have reappeared, spatially
   * nonsense, on the hedgerow's map.
   *
   * A room is a coordinate system. A zone is a set of colours. The map is drawn
   * in metres, so it keys on the room, and the fixture rooms get their own
   * sheets for the same reason rather than silently merging harness movement
   * into the farm's.
   *
   * **The exception is a room that is another room's coordinate system.**
   * `yard-dawn` is beat 6's yard, same origin, same metres (D-202), so it
   * declares `ledgerSheet: 'yard'` and her storm-night walk is on the sheet
   * she briefs beat 12 from — which is the plan's whole point about her map.
   */
  const ledgerZone: string = roomModuleFor(roomId)?.ledgerSheet ?? roomId;
  // Restoring before declarations is deliberate: ids outlive a build which did
  // not place them, then gain their current mark only when this world declares
  // them below. `readProgress` turns malformed notes into an empty snapshot, so
  // storage corruption cannot interrupt boot.
  const savedProgress = readProgress();
  const ledger = Ledger.restore(savedProgress?.ledger);
  const trace = new TickTrace();
  const traceMaxTicks = Number(params.get('trace'));
  if (Number.isSafeInteger(traceMaxTicks) && traceMaxTicks > 0) {
    const requestedScript = params.get('script') ?? 'none';
    if (!Object.hasOwn(TRACE_SCRIPTS, requestedScript)) {
      throw new Error(`unknown trace script "${requestedScript}"`);
    }
    trace.arm({
      maxTicks: traceMaxTicks,
      script: requestedScript as TraceScriptName,
      meta: { room: roomId, beat: beatWanted },
    });
    // Arm before the loop exists: a harness-started trace has a wall-clock
    // phase, while tick zero must be the same fixed point in every capture.
    trace.start();
  }

  const scene = new THREE.Scene();

  const physics = await createPhysicsWorld();

  /**
   * A box collider from the same half-extents its mesh was built from, and
   * the same rotation convention: `solidColliderDesc` builds its quaternion
   * through `solidQuaternion`, the one function `solidMeshes` traces its
   * baked `rotateX`/`rotateY` back to. This closure used to build its own
   * `THREE.Euler(pitch, yaw, 0)` (default `'XYZ'`), which silently disagreed
   * with the mesh whenever both angles were nonzero (F1,
   * `docs/engine-architecture-audit.md`).
   */
  const boxCollider = (
    half: { x: number; y: number; z: number },
    pos: { x: number; y: number; z: number },
    pitch?: number,
    collisionGroups?: number,
    yaw?: number,
  ): ReturnType<PhysicsWorld['world']['createCollider']> => {
    let desc = solidColliderDesc(physics.rapier, { half, pos, pitch, yaw });
    if (collisionGroups !== undefined) desc = desc.setCollisionGroups(collisionGroups);
    return physics.world.createCollider(desc);
  };
  let roomModule: RoomModule | null = null;
  /** The module room's live solids, for the walls on her map. A door removed is a wall gone. */
  const mapSolids = new Map<ColliderHandle, MapSolid>();
  const roomCapabilities = (): RoomCapabilities => ({
    scene,
    colliders: {
      add: (solid) => {
        const handle = boxCollider(solid.half, solid.pos, solid.pitch, passesCameraSweep(solid) ? CAMERA_PASSES : undefined, solid.yaw) as unknown as ColliderHandle;
        // Every module room's solid passes here, which makes this the one place
        // her map can learn the room's walls without a second list (D-238).
        mapSolids.set(handle, solid);
        return handle;
      },
      addHull: (points) => {
        const hull = physics.rapier.ColliderDesc.convexHull(points);
        // Degenerate corners: fail at construction rather than ship a slope she falls through.
        if (hull === null) throw new Error(`room "${roomId}": degenerate convex hull`);
        return physics.world.createCollider(hull) as unknown as ColliderHandle;
      },
      remove: (handle) => {
        mapSolids.delete(handle);
        physics.world.removeCollider(handle as unknown as ReturnType<PhysicsWorld['world']['createCollider']>, true);
      },
    },
    playOneShot,
    emitSound: (sound) => stealth?.emitSound(sound),
    lights: {
      scale: (name, factor) => {
        const entry = roomLights.get(name);
        if (entry === undefined) throw new Error(`room "${roomId}": no fixture named "${name}"`);
        entry.light.intensity = entry.base * factor;
      },
    },
    cutAmbience: () => audio.cutAmbience(),
    interaction: { setEnabled: (id, enabled) => interaction.setEnabled(id, enabled) },
    createFade: () => {
      const overlay = new FadeOverlay(0);
      moduleFades.push(overlay);
      return overlay;
    },
    createZoneBlend: (from, to, options) => {
      const blend = new Bloom(from, to, { post, scene, ...options });
      return {
        apply: (t) => blend.apply(t),
        get chromaCeiling() { return blend.census().chromaCeiling; },
      };
    },
    her: {
      get snout(): THREE.Object3D {
        const snout = rosalie.getObjectByName('snout');
        if (snout === undefined) throw new Error('her rig has no "snout" pivot');
        return snout;
      },
    },
    rigPointLights,
  });
  /** Room-owned fades, released with the rest of the DOM on context loss. */
  const moduleFades: FadeOverlay[] = [];
  /** A module room's rig's point lights, in build order (`RoomCapabilities.rigPointLights`). */
  const rigPointLights: THREE.PointLight[] = [];
  /** A module room's named fixtures and their authored intensities, for `lights.scale`. */
  const roomLights = new Map<string, { light: THREE.Light; base: number }>();
  const roomTickPlayer = { x: 0, y: 0, z: 0 };
  /** The ids nudged on the current fixed step; cleared as the step reads its events. */
  const tickNudges = new Set<string>();
  /** The ids rooted on the current fixed step: an edge, as `tickNudges` is. */
  const tickRoots = new Set<string>();
  const roomTickMutable = {
    dt: 0,
    player: roomTickPlayer,
    raiseFlag: (flag: string) => storyFlags.add(flag),
    completed: (id: string) => interaction.completed(id),
    nudged: (id: string) => tickNudges.has(id),
    called: false,
  };
  const roomTick: RoomTick = roomTickMutable;

  // `SpawnPoint`, not a bare position: beat 1's spawn carries a facing, and a
  // structural type without it would silently drop the field on assignment.
  let spawn: SpawnPoint;
  let pausePalette: ZonePalette;
  if (roomModuleFor(roomId) !== null) {
    // ── Every room that is a module: all but the test room and the fixture ──
    //
    // The room declares its palette, rig, sky, spawn and ambience
    // (`RoomModuleDefinition.environment`); this branch only applies them, so
    // adding a room adds a module rather than another branch here. Each room's
    // rig moved into its module with its reasoning.
    const definition = roomModuleFor(roomId);
    if (definition === null) throw new Error(`room "${roomId}": module missing`);
    const environment = definition.environment({ part: partNumber, beat: beatNumber });
    const zone = environment.zone;
    const rig = createZoneLighting(zone, environment.lighting);
    scene.add(rig.group);
    // Held so beat 6's strike can drive the irradiance — D-107's second
    // reader. The base is read back off the light rather than restated, so
    // the room's authored `hemiIntensity` stays the single statement of how
    // dark it is when nothing is happening.
    roomHemi = rig.hemi;
    roomHemiBase = rig.hemi.intensity;
    rig.group.traverse((object) => {
      if (object instanceof THREE.Light && object.name.length > 0) roomLights.set(object.name, { light: object, base: object.intensity });
      if (object instanceof THREE.PointLight) rigPointLights.push(object);
    });
    const background = environment.background;
    if (background instanceof THREE.Color) {
      scene.background = background;
    } else if (background.kind === 'sky') {
      // An exterior's sky is the one thing a flat colour cannot be: the horizon
      // is the zone's own fog value, or the far side grows a seam (D-106).
      roomSky = createSky(background.zone ?? zone, {
        seed: background.seed,
        ...(background.zenith === undefined ? {} : { zenith: background.zenith }),
        ...(background.middle === undefined ? {} : { middle: background.middle }),
      });
      scene.background = roomSky.background;
    } else {
      scene.background = rig.background;
    }
    const requestedOut = params.get('out');
    // `Number('')` and `Number('   ')` are both zero. A query that omitted its
    // value is not a zero-result run, so only a non-blank spelling may override
    // the checkpoint's outcome.
    const parsedOut = requestedOut === null || requestedOut.trim().length === 0
      ? null
      : validatedOut(Number(requestedOut));
    // `?battens=N`, the farrowing hall's cull experiment (S1): see
    // `battenCull` in `src/game/rooms/farrowing.ts`. Not a number is absent.
    const requestedBattens = Number.parseInt(params.get('battens') ?? '', 10);
    const battenBudget = Number.isFinite(requestedBattens) ? requestedBattens : undefined;
    roomModule = definition.create({
      part: partNumber,
      beat: beatNumber,
      dressing: params.get('dress') !== '0',
      out: parsedOut ?? effectiveOut(savedProgress?.out),
      ...(battenBudget === undefined ? {} : { battens: battenBudget }),
    }, roomCapabilities());
    disposeRoom = roomModule.dispose.bind(roomModule);
    audio.setAmbience(environment.ambience);
    if (params.get('post') !== '0') {
      post = createPostStack(renderer, {
        width: canvas.clientWidth || window.innerWidth,
        height: canvas.clientHeight || window.innerHeight,
        pixelRatio: renderer.getPixelRatio(),
        zone,
      });
    }
    // A constant for the room (beat 6's and beat 7's storm), advanced on the render
    // step; only a room that declared rain sets the flag. See `weatherActive`.
    if (environment.rain !== undefined && environment.rain > 0) {
      weatherActive = true;
      if (post) post.setRain(environment.rain);
    }
    spawn = environment.spawn;
    pausePalette = zone;
  } else if (roomId === 'testroom') {
    // The grey-box room is a measuring instrument and is lit like one: neutral,
    // no haze, no grade. It still needs a zone, because the toon material's
    // bands are anchored to one — without it every surface clamps to the top
    // band and the room renders as flat white.
    applyZone(STUDIO_ZONE);
    setExposureReference(3.1);
    scene.background = new THREE.Color(0x1b1e23);
    scene.add(createTestRoom());
    scene.add(createTestLighting());

    for (const solid of testRoomSolids()) boxCollider(solid.half, solid.pos, solid.pitch);

    // The ramp is a convex hull of the same six corners the mesh is built from,
    // so what you can see and what she can walk on are the same solid.
    for (const w of testRoomWedges()) {
      const points = new Float32Array(wedgeCorners(w).flat());
      const hull = physics.rapier.ColliderDesc.convexHull(points);
      if (hull) physics.world.createCollider(hull);
    }

    spawn = SPAWN;
    pausePalette = STUDIO_ZONE;
  } else {
    // Like `?room=testroom`, this is a measuring instrument rather than a
    // shipped scene. Its sealed trap and tight escape lane are deliberately
    // authored geometry so the softlock regression gate can prove both verdicts.
    applyZone(STUDIO_ZONE);
    setExposureReference(3.1);
    scene.background = new THREE.Color(0x1b1e23);
    scene.add(createSoftlockFixtureRoom());
    scene.add(createTestLighting());
    for (const solid of softlockFixtureSolids()) boxCollider(solid.half, solid.pos);
    spawn = SOFTLOCK_FIXTURE_SPAWN;
    pausePalette = STUDIO_ZONE;
  }

  /**
   * D-002's wear, set once, from `COLOUR_ARC` (D-116).
   *
   * **Beat 7 is set to beat SIX's value here and steps to its own at the ditch.**
   * The table is the value *during* a beat, and that one exception is the whole
   * reason the channel exists: she goes into the water at one value and comes out
   * at another, in a beat that then holds a camera on her for ninety seconds.
   * `OutDirector` owns the ramp; everything else in the game is this one line.
   *
   * `wearForBeat` returns 0 for beats outside the table, which is `?beat=0` and
   * every workbench entry — correct rather than defensive, because those are
   * measuring instruments and a subject worn to some story position is the wrong
   * pig to measure.
   */
  setWear(wearForBeat(beatNumber === 7 ? 6 : beatNumber));
  // Beat 8 carries the field's colour back into both of its room parts. The
  // rooms retain their own palette lighting, but the grade ceiling comes from
  // the one colour-arc row that names this beat; earlier beats keep their
  // established zone ceilings unchanged.
  // Beat 9 has no override: wool-shed already owns the matching 0.30 ceiling.
  if (beatNumber === 8) {
    const chroma = chromaForBeat(beatNumber);
    if (chroma !== null) post?.setChromaCeiling(chroma);
  }
  const rosalie = rosalieAsset.create();
  rosalie.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });
  // Three nodes, because the roll has to spin about her BODY, not her feet.
  //
  // Every asset's origin is its ground contact point (docs/asset-conventions),
  // so rotating the mesh directly swung her whole body through the floor — she
  // "sank into the ground" while rolling, which is what it looked like because
  // it is what was happening.
  //
  //   rosalieRoot  position + facing
  //     rollNode   raised to her centre, spins about X
  //       rosalie  pushed back down by the same amount
  //
  // With `rollSpin` at zero the two offsets cancel exactly, so nothing about
  // standing, walking or the rig changes.
  const rosalieRoot = new THREE.Group();
  rosalieRoot.name = 'rosalie-root';
  const rollNode = new THREE.Group();
  rollNode.name = 'rosalie-roll';
  rollNode.position.y = ROLL_PIVOT_Y;
  rosalie.position.y = -ROLL_PIVOT_Y;
  rollNode.add(rosalie);
  rosalieRoot.add(rollNode);
  scene.add(rosalieRoot);

  const character = new CharacterController(physics, ROSALIE_TUNING, spawn);
  const camera = new SpringArmCamera(1, ROSALIE_CAMERA);
  // Start the rig behind her rather than at world +Z.
  //
  // `#yaw` initialised to 0 while she spawns at a facing of −0.60 (D-058) leaves
  // the camera off her axis at boot, and the idle auto-recentre then spends the
  // first seconds of the game slowly swinging round to catch up. Two costs, one
  // of them measurable: the drift is visible on the hand-over, and it makes the
  // `born-zero-input` golden trace **non-reproducible** — the recentre runs on
  // the render step, so what `fixedUpdate` samples as its movement basis depends
  // on frame timing. That is exactly the boundary D-045 records, and it arrived
  // the moment her facing stopped being zero. Seeding the yaw removes both.
  camera.setYaw(spawn.facing ?? 0);

  const rig = new RosalieRig(rosalie);
  const animation = new AnimationPlayer(ROSALIE_CLIPS, ROSALIE_ANIM);
  const perception = new PerceptionSolver(ROSALIE_PERCEPTION);
  const pose = neutralPose();
  /**
   * The perception target set by hand through the game handle.
   *
   * Kept as a variable rather than pushed straight into the solver, because the
   * interaction system now writes that channel every render frame and would
   * overwrite anything the harness set. The two are ordered rather than merged:
   * what she can act on beats what a test told her to look at, because on the
   * one frame both have an opinion the player's snout should follow the player.
   */
  let manualPerception: PerceptionTarget | null = null;

  const input = new Input();
  input.attach();

  // ── Act 1 beat 1 ───────────────────────────────────────────────────────────
  //
  // Three objects and one rule about how they talk to each other:
  //
  //   InteractionSystem  what her snout is pointing at, and what she does to it
  //   BeatSequencer      the authored beat, which knows nothing about the world
  //   BornDirector       what a cue MEANS in a scene made of triangles
  //
  // The sequencer emits cues and the director stages them. Nothing in
  // `src/story/` may reach a mesh, which is why the beat replays in Node.
  const interaction = new InteractionSystem();
  /**
   * Flags the GAME raises, as opposed to completions the interaction system
   * reaches on its own.
   *
   * There is one, and it exists because `nudge` has no duration and so never
   * completes — which is right, since nudging is not a task. The beat asks
   * "has her mother answered", the interaction system can only say "has this
   * hold finished", and this set is the join between the two questions.
   */
  const storyFlags = new Set<string>();
  let story: BeatSequencer | null = null;
  let beatPartModule: BeatPartModule | null = null;
  /**
   * Physics mutations a part requested off the fixed step (`atNextTick`),
   * run and traced at the very start of the next one. One per kind, so a
   * repeated request is the latch it replaced (beat 1's taken littermate).
   */
  const pendingTickEffects = new Map<Exclude<TraceExternalEvent['kind'], 'teleportPlayer'>, () => void>();
  // Registry lookup is pure: it constructs nothing until the host creates the
  // module below.
  const registeredBeatPart = beatPartModuleFor(beatNumber, partNumber, roomId);
  const beatPartPlayer = { x: 0, y: 0, z: 0 };
  /** `BeatPartTick.motion`, refilled before `afterMove` and `fixedStep`. */
  const beatPartMotion = { gait: 'still' as Gait, speed: 0, grounded: true, dx: 0, dz: 0 };
  /** Her state into the borrowed tick record, from the fixed step's `current` and `previous`. */
  const syncBeatPartTick = (dt: number): void => {
    beatPartTickMutable.dt = dt;
    beatPartPlayer.x = current.x;
    beatPartPlayer.y = current.y;
    beatPartPlayer.z = current.z;
    beatPartMotion.gait = current.gait;
    beatPartMotion.speed = current.speed;
    beatPartMotion.grounded = current.grounded;
    beatPartMotion.dx = current.x - previous.x;
    beatPartMotion.dz = current.z - previous.z;
  };
  const beatPartTickMutable = {
    dt: 0,
    player: beatPartPlayer,
    motion: beatPartMotion,
    raiseFlag: (flag: string): void => {
      storyFlags.add(flag);
    },
    hasFlag: (flag: string): boolean => storyFlags.has(flag),
    moveSelf: (to: SpawnPoint): void => {
      character.reset(to);
      current = character.state;
      previous = current;
      // Keep the borrowed record true for the rest of the call that moved her
      // (beat 4's capture respawns her from inside `afterMove`).
      syncBeatPartTick(beatPartTickMutable.dt);
    },
    nudged: (id: string): boolean => tickNudges.has(id),
    rooted: (id: string): boolean => tickRoots.has(id),
    called: false,
  };
  const beatPartTick: BeatPartTick = beatPartTickMutable;
  /** Her interpolated position for `BeatPartModule.renderStep`, reused each frame. */
  const renderHer = { x: 0, y: 0, z: 0 };
  // ── Where to go next (D-209) ─────────────────────────────────────────────
  // One `Wayfinder` for every beat: the goal comes from `wayGoals.ts` keyed
  // by the stage the story is in, so a beat opts in with a table row and not
  // with a branch here.
  const wayfinder = new Wayfinder();
  const wayContextSelf = { x: 0, y: 0, z: 0 };
  // Completions too: a stage that waits on a verb points at the next one only
  // once this one is done, and a verb's completion is not a story flag (beat
  // 9's pallet). Beat 8.2's crates raise flags for this and predate it.
  const wayContext: WayContext = {
    self: wayContextSelf,
    has: (flag) => storyFlags.has(flag) || interaction.completed(flag),
  };
  let wayfinderTicked = false;
  let interactables: readonly Interactable[] = [];
  /** Set once the handover has been committed, so it can only happen once. */
  /** Whether this beat has already written its checkpoint. Boundaries commit once. */
  let boundaryCommitted = false;

  /**
   * The stopwatch, and the hidden panel that shows it.
   *
   * Armed for every beat including `?beat=0`, because the gesture is four taps
   * in a corner nobody touches and a run that turns out to be worth timing is
   * not always one you knew was worth timing when you started it.
   */
  const timeline = new PlaythroughTimeline(beatNumber);
  disarmDevPanel = armDevPanel({ beat: beatNumber, part: partNumber, timeline });
  let chaining = false;
  /** Seconds of rendered time since the running beat finished. See `CHAIN_MAX_WAIT`. */
  let sinceBeatEnded = 0;

  // ── Act 1 beat 2 ───────────────────────────────────────────────────────────
  //
  // Deliberately a second set of nullable locals rather than a `Director`
  // interface the two beats implement. They do not have the same shape and
  // pretending otherwise would be the expensive kind of tidiness: beat 1 holds
  // the camera and frames an authored shot, beat 2 never takes the camera at all;
  // beat 2 owns a worker, a stealth field and a gate, beat 1 owns none of them.
  // An interface wide enough for both is an interface that describes neither, and
  // `docs/backlog.md` files the real selector against beat 3 — the beat that also
  // makes the zone contract honest, and the one where a third case turns two
  // coincidences into a pattern.
  /**
   * The chapter card, when this beat was arrived at by handing over from the one
   * before it. Null on a cold boot and on every `?beat=N` typed by hand.
   *
   * **That distinction is the whole design.** Spec §7's cards mark a passage, and
   * there is no passage when somebody opens beat 3 directly — there is also no
   * passage when a spec boots one, which is why every existing test is untouched
   * by this and why the parameter is set by the hand-over rather than by `BEATS`.
   *
   * Alex, playing beats 1–3 in order: *"there is not really anything to do to
   * progress and then Rosalie is suddenly in the next room."* This is the second
   * half of that sentence. See `src/ui/chapterCard.ts`.
   */
  let chapterCard: ChapterCard | null = (() => {
    if (params.get('chained') !== '1') return null;
    const mark = chapterMarkForBeat(beatNumber);
    return mark === null ? null : new ChapterCard(mark);
  })();

  /** Reused every fixed step so the ray query allocates nothing. See `hasLineOfSight`. */
  const sightRay = new physics.rapier.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 1 });
  /**
   * Can this eye reach that back? The world's half of the stealth seam, and
   * the one statement of it for beats 4, 6, 10 and every module room — they
   * were three byte-equivalent copies until session 66. Beat 2's differs (it
   * forgives a hit within 2 cm of her) and builds it from `rapierRayHit`.
   *
   * The epsilon because a ray that ends ON her would report itself blocked by
   * the thing it is looking at; her own collider excluded for the same reason
   * when she stands against a wall.
   */
  const rapierRayHit = (from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }): number | null => {
    const dx = to.x - from.x; const dy = to.y - from.y; const dz = to.z - from.z;
    const distance = Math.hypot(dx, dy, dz);
    if (distance < 0.001) return null;
    sightRay.origin.x = from.x; sightRay.origin.y = from.y; sightRay.origin.z = from.z;
    sightRay.dir.x = dx / distance; sightRay.dir.y = dy / distance; sightRay.dir.z = dz / distance;
    return physics.world.castRay(sightRay, distance, true, undefined, undefined, character.collider)?.timeOfImpact ?? null;
  };
  const rapierLineOfSight: HasLineOfSight = (from, to) => rapierRayHit(from, to) === null;
  /** Her audible steps, once per stride, for whichever room is listening (`footfallStride.ts`). */
  const herFootfalls = new FootfallStride();

  if (registeredBeatPart !== null) {
    const module = registeredBeatPart.create({
      createFade: (cues) => new BeatFadeDirector(cues),
      world: {
        scene,
        playOneShot: (id, options) => audio.engine.playOneShot(id, options),
        playLoop: (id, options) => audio.engine.playLoop(id, options),
        createBloom: (from, to, options) => new Bloom(from, to, { post, scene, ...options }),
        interaction: {
          setEnabled: (id, enabled) => interaction.setEnabled(id, enabled),
          moveTo: (id, at) => interaction.moveTo(id, at),
          completed: (id) => interaction.completed(id),
        },
        colliders: {
          add: (solid) => roomCapabilities().colliders.add(solid),
          setPose: (handle, pose) => {
            // A moving leaf (beat 2's gate, beat 4's door) is not a wall on her
            // map: its solid is the pose it was added at, and would be drawn
            // shut after it swung open.
            mapSolids.delete(handle);
            const collider = handle as unknown as ReturnType<PhysicsWorld['world']['createCollider']>;
            collider.setTranslation(pose.position);
            collider.setRotation(pose.quaternion);
          },
          remove: (handle) => roomCapabilities().colliders.remove(handle),
        },
        cutAmbience: () => audio.cutAmbience(),
        lineOfSight: (from, to) => rapierLineOfSight(from, to),
        rayHit: (from, to) => rapierRayHit(from, to),
        atNextTick: (kind, effect) => { pendingTickEffects.set(kind, effect); },
        stormFlash: (flash, hemiScale, backgroundIntensity) => {
          if (post) post.setStormFlash(flash);
          if (roomHemi) roomHemi.intensity = roomHemiBase * hemiScale;
          scene.backgroundIntensity = backgroundIntensity;
        },
        // The room's typed handles — beat 7's and 10's bay, beat 8's fuse and
        // lights — and nothing else of the room.
        room: roomSeamsOf(roomModule),
      },
    });
    validateBeatPartModule(module, registeredBeatPart);
    beatPartModule = module;
    // A part that builds its own watchers (beat 10's dog): the host feeds it her
    // footfalls through `emitSound` and reports it; the part steps it.
    if (module.stealth !== undefined) stealth = module.stealth;
  }
  if (beatPartModule !== null) {
    story = beatPartModule.story;
    interactables = [...(roomModule?.interactables ?? []), ...beatPartModule.interactables];
    interaction.setInteractables(interactables);
    // Nulled as well as disposed, so a debug getter reading the part after a
    // context loss reports nothing rather than a torn-down field.
    disposeBeat = (): void => {
      beatPartModule?.dispose();
      beatPartModule = null;
    };
  } else {
    // No part for this beat in this room (beat 0, `?room=` fixtures): the
    // room's own interactables only, so a room-only boot stays inert.
    interactables = roomModule?.interactables ?? [];
    interaction.setInteractables(interactables);
  }
  // A watched module room: the room declares grid, cover and watchers; the
  // host supplies the one thing it cannot, the Rapier line-of-sight query.
  if (roomModule?.stealth !== undefined) {
    const roomStealth = roomModule.stealth;
    stealth = new StealthSystem(roomStealth.grid, roomStealth.cover, roomStealth.watchers, (from, to) => rapierLineOfSight(from, to));
  }
  // The interaction system remains story-blind: it emits an `observe` event and
  // owns the current target position, while this one integration seam gives a
  // restored id the map meaning the current world declares for it. Declarations
  // do not create observations, so an unobserved farm remains an empty ledger.
  for (const interactable of interactables) {
    if (!interactable.verbs.includes('observe')) continue;
    const mark = interaction.markOf(interactable.id);
    if (mark !== null) ledger.declare({ id: interactable.id, zone: ledgerZone, mark });
  }

  /**
   * The sheet she raises, built from the ledger at the moment she raises it.
   *
   * **One zone, not the farm** (D-137). Every room in this game is built at its
   * own origin and there is no authored layout of rooms relative to one another,
   * so a farm-wide sheet would mean inventing a site plan and then keeping it
   * true against six rooms that were never built to agree about where they are.
   * Beat 12 briefs off the yard, which is one room.
   *
   * Built per raise rather than held, because a sheet held across a raise would
   * be a second copy of the ledger going stale in a closure.
   */
  const mapSheet = (): MentalMapSheet => {
    const plan = beatPartModule?.planMarks?.() ?? [];
    const wash = ledger.wash(ledgerZone);
    return {
      wash,
      marks: ledger.observations(ledgerZone),
      cellMetres: WASH_CELL_METRES,
      walls: wallStrokes([...mapSolids.values()], wash, WASH_CELL_METRES),
      trail: tracedRoutes(wash, WASH_CELL_METRES),
      ...(plan.length > 0 ? { plan } : {}),
    };
  };

  let paused = false;
  /** The loop's clock intentionally never pauses; this is the game clock tests and systems observe. */
  let pausedSimTime = 0;
  let pauseOverlay: PauseOverlay | null = null;
  const setPaused = (nextPaused: boolean): void => {
    if (paused === nextPaused) return;
    paused = nextPaused;
    // Freeze interpolation at the state already on screen. Leaving an older
    // `previous` value here lets a paused render continue one last visible slide.
    if (paused) previous = current;
    // Resuming is not falling behind: discard the wall-clock time that passed
    // while the game was gated, or the loop pays it back as one large catch-up
    // step. See LoopHandle.dropBacklog.
    else loop?.dropBacklog();
    pauseOverlay?.setPaused(paused);
    void audio.setPaused(paused);
  };
  const toggleMuted = (): void => {
    const muted = audio.toggleMute();
    try {
      window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      // The toggle still works for this session; only the memory is lost.
    }
  };
  pauseOverlay = new PauseOverlay(pausePalette, {
    onResume: () => setPaused(false),
    // Restart is a navigation, exactly like the hand-over and like Continue
    // (D-126). There is one way into a beat and this is it — an in-place
    // teardown would be a second construction path that only this button
    // exercises, which is the shape of failure this project keeps repeating.
    //
    // Straight to beat 1 rather than to the title: the player has just said
    // what they want, and answering them with a splash screen and a content
    // notice would be the menu asking them to say it twice.
    onRestart: () => {
      clearProgress();
      window.location.replace(`${window.location.pathname}?beat=1`);
    },
  });
  pauseOverlay.attach();

  // Input clears held keys on blur, but that alone only prevents stuck movement.
  // Without pausing as well, returning from another app found Rosalie farther
  // along a wall than the player left her, especially on installed tablet PWAs.
  window.addEventListener('blur', () => setPaused(true));
  // And on hide. A phone switching apps, or locking, does not reliably blur
  // the window first; the audio engine already stops on this same event, so
  // without it the mix went quiet while the game kept walking her.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') setPaused(true);
  });

  /**
   * The gesture that is allowed to start the mix.
   *
   * Browsers will not let a page make sound until the user has interacted with
   * it, and the click that captures the pointer is the one gesture every player
   * performs before doing anything else — so the two are deliberately the same
   * event rather than a separate "click to enable audio" prompt, which would be
   * one more thing between the player and the game.
   *
   * `audio` itself is constructed much earlier, next to the renderer. See there.
   */
  canvas.addEventListener('click', () => {
    void canvas.requestPointerLock();
    void audio.start();
  });

  /**
   * Touch controls, for the tablet the game was previously unplayable on.
   *
   * Attached unconditionally — `TouchControls` decides for itself whether the
   * device has a real touchscreen and stays entirely absent otherwise, which is
   * why there is no capability check here. Doing that test in one place matters:
   * a second, subtly different check at the call site is how an on-screen
   * joystick ends up appearing on somebody's desktop.
   *
   * It also starts the mix, because on a tablet there is no click to capture a
   * pointer with, so the first touch is the only user gesture the browser will
   * ever offer.
   */
  const touch = new TouchControls(input);
  touch.attach(canvas);
  const verbPrompt = new VerbPrompt();
  disposeVerbPrompt = (): void => verbPrompt.dispose();
  /**
   * Her map (spec §5.3). Constructed once and kept, like the prompt: it is one
   * empty `<div>` until something raises it, and building it per raise would
   * put a document mutation on the frame the player pressed the key.
   */
  const mentalMap = new MentalMap();
  let raisedSheet: 'hers' | 'his' | null = null;
  disposeMentalMap = (): void => mentalMap.dispose();
  let promptState: PromptState = { verb: null, progress: 0, suppressed: true, touch: touch.enabled };
  // Listening on the DOCUMENT, not the canvas.
  //
  // The overlay's action buttons are appended to `document.body` — they have to
  // be, since they are fixed-position UI rather than scene content — so a touch
  // on Roll or Creep never bubbles through the canvas at all. Bound to the
  // canvas, this listener missed exactly the case where a player's FIRST touch
  // lands on a button, and that player then got a silent game for the whole
  // session, because a browser will only grant audio on a real user gesture and
  // there is no second chance to catch that one. Found on independent review.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType === 'touch') void audio.start();
    },
    { passive: true },
  );

  /**
   * Camera collision sweep. The camera module is deliberately physics-free, so
   * this is the one adapter between it and Rapier.
   *
   * `castShape` with a ball is a genuine volume sweep rather than a ray, which
   * is what stops the lens slipping through a thin wall between two rays.
   */
  const sweepShape = new physics.rapier.Ball(ROSALIE_CAMERA.collisionRadius);
  const sweepPos = { x: 0, y: 0, z: 0 };
  const sweepVel = { x: 0, y: 0, z: 0 };
  const sweepRot = { x: 0, y: 0, z: 0, w: 1 };
  // The radius argument is intentionally not read: the sweep shape is built once
  // from the same tuning value, because allocating a Rapier Ball every frame to
  // re-state a constant would be pure garbage-collector pressure.
  const sweep = (from: THREE.Vector3, dir: THREE.Vector3, maxDistance: number): number | null => {
    sweepPos.x = from.x;
    sweepPos.y = from.y;
    sweepPos.z = from.z;
    sweepVel.x = dir.x;
    sweepVel.y = dir.y;
    sweepVel.z = dir.z;
    const hit = physics.world.castShape(
      sweepPos,
      sweepRot,
      sweepVel,
      sweepShape,
      0,
      maxDistance,
      true,
      undefined,
      SWEEP_GROUPS,
      character.collider,
    );
    return hit ? hit.time_of_impact : null;
  };

  const resize = (): void => {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    // Recomputed on resize, not only at boot: dragging a window onto a 4K
    // display is exactly the case the budget exists for.
    renderer.setPixelRatio(boundedPixelRatio(w, h, window.devicePixelRatio));
    renderer.setSize(w, h, false);
    // The post stack owns its own render targets, so it has to be told — the
    // renderer resizing does not reach them, and the symptom of forgetting is a
    // correctly-lit scene sampled through a stale, wrongly-sized buffer.
    post?.setSize(w, h, renderer.getPixelRatio());
    camera.setAspect(w / h);
  };
  resize();
  window.addEventListener('resize', resize);

  let frameTimeMs = 16.7;
  let samples: number[] = [];

  // Two states so the RENDER step can interpolate between them. Without this she
  // moves in 60 Hz staircases on a 144 Hz display, which reads as judder and
  // would be mistaken for an animation problem.
  let previous = character.state;
  let current = character.state;
  let pendingTeleport: { x: number; y: number; z: number; facing?: number } | null = null;

  const basis = { forwardX: 0, forwardZ: 1, rightX: 1, rightZ: 0 };
  let traceTick = 0;
  const traceBodyTransforms = (): TraceBodyTransform[] => {
    const bodies: TraceBodyTransform[] = [];
    physics.world.bodies.forEach((body) => {
      const translation = body.translation();
      const rotation = body.rotation();
      bodies.push({
        handle: body.handle,
        translation: { x: translation.x, y: translation.y, z: translation.z },
        rotation: { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w },
      });
    });
    return bodies;
  };
  /** Largest landing impact since the last rendered frame. See the latch below. */
  let pendingLandingImpact = 0;
  /** Accumulated roll rotation, radians. See ROLL_CIRCUMFERENCE. */
  let rollSpin = 0;
  /**
   * True while a BEAT's authored framing is the thing driving the lens.
   *
   * A latch rather than a per-frame test, because `releaseShot(true)` starts an
   * eased return and calling it again every frame restarts that ease from the
   * current position — which never arrives, and reads as a camera that will not
   * settle.
   *
   * ── It used to be `releaseWhenBeatHandsOver`, and it only ever freed beat 1 ──
   *
   * The old latch was one-shot (`= false` after the first release) and its
   * release branch also required `director`, which is beat 1's `BornDirector`
   * and is **null in every other room**. So the four beats that take the camera
   * through `cull`/`yard`/`out` framings could enter `overrideShot` and had no
   * path out of it at all: `SpringArmCamera.update()` returns immediately while
   * `#overridden`, so the lens stopped dead at the authored position and stayed
   * there for the rest of the beat while she walked away from it.
   *
   * That is exactly what shipped. Beat 6 held `BIN_CAMERA` for its 4 s climb-out
   * and then froze inside the bin — Alex played 200 s of the yard looking at the
   * sky, a corner of the truck and the top of a pallet stack, with Rosalie not in
   * frame once. Beat 7 held `OPEN_CAMERA` for 3 s and froze under the trailer for
   * the whole beat. Both beats were unplayable and every gate was green, because
   * the specs drive stages and read `outDebug`/`yardDebug` and **nothing had ever
   * asserted that the lens follows her** (D-100, again).
   *
   * So the latch now tracks the thing it is actually about — *does a beat want
   * the camera right now* — and it is set and cleared by the framing itself, for
   * every director, in the one place a beat's camera enters the render step.
   * `shot()`'s override is untouched: it does not set this flag, so it is never
   * released by it.
   */
  let beatHoldsCamera = false;
  /** Scratch state handed to the animator, so the latched impact can differ. */
  const animState: CharacterState = { ...character.state };
  const shotPosition = new THREE.Vector3();
  const shotLookAt = new THREE.Vector3();
  /** Scratch for the audio listener and mix frame — reused, never reallocated. */
  const listenerPosition = { x: 0, y: 0, z: 0 };
  /** The module room's view of the frame: the settled lens, which is the listener. */
  const roomView: RoomView = { camera: listenerPosition };
  const listenerForward = new THREE.Vector3(0, 0, -1);
  const audioFrame: AudioFrame = {
    gait: 'still',
    speed: 0,
    grounded: true,
    landingImpact: 0,
    scramble: 0,
    cycles: 0,
    rollCircumference: ROLL_CIRCUMFERENCE,
    position: { x: 0, y: 0, z: 0 },
  };

  /**
   * What the beat is allowed to see: interaction completions plus game flags.
   *
   * A fresh Set per fixed step, which is 60 small allocations a second and is
   * worth it — the alternative is a mutable Set the sequencer holds a reference
   * to, and a stage that captured it would then see the future.
   */
  /**
   * Both beats' ids, unconditionally.
   *
   * Only one beat's interactables are ever registered, so the other's ids simply
   * report `false` and 0 — and asking for them costs a Map lookup that misses.
   * Branching on the running beat here would put a third place that has to know
   * which beat is live, and the two that already do (the boot block and
   * `stageCue`) are the two that have to.
   */
  const STORY_IDS: readonly string[] = [
    // A part module's verbs are story facts too. Without this beat 10's count
    // never reached `the-count`: the ledger took the mark and the stage waited
    // out `COUNT_PATIENCE` (found by its wayfinding goal pointing at a huddle
    // already counted; `cold-spawn.spec.ts`'s beat 10 route is the gate).
    ...(beatPartModule?.interactables.map((interactable) => interactable.id) ?? []),
  ];

  function storyCompletions(): ReadonlySet<string> {
    const all = new Set(storyFlags);
    for (const id of STORY_IDS) {
      if (interaction.completed(id)) all.add(id);
    }
    return all;
  }

  function storyProgress(): ReadonlyMap<string, number> {
    const progress = new Map<string, number>();
    for (const id of STORY_IDS) progress.set(id, interaction.progressOf(id));
    return progress;
  }

  /**
   * A cue goes to the beat part and to the room. Each owns both halves of
   * what the cue means: what she can DO (interactables, the simulation) and
   * what the frame LOOKS like (its director).
   */
  function stageCue(cue: { id: string }): void {
    beatPartModule?.handleCue(cue);
    roomModule?.handleCue?.(cue);
  }

  /** Spec §4: the camera drops to snout height in crouch and chases low in a roll. */
  function cameraModeFor(state: CharacterState, x: number, z: number): CameraMode {
    // The pen wins over every gait, including a roll.
    //
    // R-014: a creep is 0.54 m deep and the roll framing is a 2.5 m arm. Letting
    // a gait pick the framing inside a pen is how the beat ended up being played
    // against the inside face of a partition for three minutes.
    if (roomModule?.underfoot?.(x, z).pen === true) return 'pen';
    if (state.gait === 'roll') return 'roll';
    if (state.gait === 'creep') return 'crouch';
    return 'follow';
  }

  if (contextLost) {
    // The context died while boot was still assembling the scene. Starting a
    // loop now would run a simulation against a dead renderer for ever, and the
    // overlay is already up.
    return;
  }

  /**
   * The complete simulation tick, shared by RAF and the test-only manual path.
   *
   * `input` is its snapshot: callers may prepare it through Input's existing
   * synthetic seams, then advance exactly one tick through the loop. Keeping
   * trace drive and record inside this callable body means the golden recorder
   * still brackets every tick from either driver.
   */
  const fixedUpdate = (dt: number): void => {
    const activeTrace = trace.drive(input, traceTick);
    for (const [kind, effect] of pendingTickEffects) {
      effect();
      trace.recordExternalEvent({ tick: traceTick, kind });
    }
    pendingTickEffects.clear();
    if (pendingTeleport !== null) {
      const teleport = pendingTeleport;
      pendingTeleport = null;
      character.reset(teleport);
      current = character.state;
      previous = current;
      trace.recordExternalEvent({
        tick: traceTick,
        kind: 'teleportPlayer',
        position: { x: teleport.x, y: teleport.y, z: teleport.z },
        facing: current.facing,
      });
    }
    let sampledAxis: { x: number; y: number } | null = null;
    const traceCueIds: string[] | null = activeTrace === null ? null : [];
    if (input.pressed('pause')) setPaused(!paused);

    // The camera is the frame of reference for movement (spec §4: WASD is
    // camera-relative), so its basis is resolved before she is moved by it.
    if (!paused) {
      if (!trace.movementBasis(basis)) camera.getMoveBasis(basis);
      const axis = input.moveAxis();
      sampledAxis = axis;
      const moveX = basis.forwardX * axis.y + basis.rightX * axis.x;
      const moveZ = basis.forwardZ * axis.y + basis.rightZ * axis.x;

      // The boards have to break before this tick's controller movement resolves.
      // Once Rapier has met the intact collider it correctly reports no movement
      // and therefore no speed, which is too late to distinguish a roll from a
      // walk. `current` is the previous fixed step's simulation state, so this is
      // the real arriving roll, not an input-only guess or a scripted placement.
      beatPartModule?.beforeMove?.(current, beatPartTickMutable.raiseFlag);

      previous = current;
      // D-209: a live wayfinding look turns an idle pig to face the goal. It
      // reads last tick's look — the wayfinder is stepped after the story,
      // below — which is one tick of a 2.5 s look and changes nothing.
      const wayFace = wayfinder.turnTo(current);
      current = character.update(dt, {
        moveX,
        moveZ,
        crouch: input.down('sneak'),
        roll: input.down('roll'),
        ...(wayFace === null ? {} : { face: wayFace }),
      });
      // Fixed-step rather than render-step: a 144 Hz display must not remember
      // more route than a 60 Hz one, and `consume()` below must remain last so
      // an edge-triggered observe cannot disappear before interaction reads it.
      ledger.visit(ledgerZone, current.x, current.z);

      // Latch the landing impulse for the renderer.
      //
      // `landingImpact` is non-zero on EXACTLY ONE fixed step, by design — the
      // animator needs a clean single-step impulse, not a decaying value. But
      // animation runs on the render step, so whenever two sim steps fall
      // between two rendered frames (any display slower than 60 Hz, or one
      // stutter) the render never observes it and the landing silently does not
      // play. Holding the largest impact seen since the last frame is what makes
      // the two rates safe to mix.
      if (current.landingImpact > pendingLandingImpact) {
        pendingLandingImpact = current.landingImpact;
      }

      // After the controller and before interaction sampling (beat 4: its
      // patrols move here so `observe` samples where they are this tick).
      if (beatPartModule?.afterMove !== undefined) {
        syncBeatPartTick(dt);
        beatPartModule.afterMove(beatPartTick);
      }

      // ── Beat 7 ────────────────────────────────────────────────────────────

      // `interaction` reads the input BEFORE `input.consume()` at the bottom
      // of this step, which is what makes a nudge fire exactly once.
      const events = interaction.update(
        dt,
        { x: current.x, y: current.y, z: current.z, facing: current.facing },
        {
          interactDown: input.down('interact'),
          interactPressed: input.pressed('interact'),
          observeDown: input.down('observe'),
        },
      );
      tickNudges.clear();
      tickRoots.clear();
      for (const event of events) {
        if (event.kind === 'observe') {
          const mark = interaction.markOf(event.id);
          if (mark !== null && !ledger.has(event.id)) notesRecorded += 1;
          if (mark !== null) ledger.record({ id: event.id, zone: ledgerZone, mark });
        }
        if (event.kind === 'nudge') tickNudges.add(event.id);
        if (event.kind === 'root') tickRoots.add(event.id);
      }

      if (roomModule !== null) {
        roomTickMutable.dt = dt;
        // Read upstream of `input.consume()` like every edge; see beat 10's `F` below.
        roomTickMutable.called = input.pressed('callHolly');
        roomTickPlayer.x = current.x;
        roomTickPlayer.y = current.y;
        roomTickPlayer.z = current.z;
        roomModule.fixedStep(roomTick);
        // The room has her body (`RoomModule.carry`): a shot, not a step.
        const carried = roomModule.carry;
        if (carried !== null && carried !== undefined) {
          character.reset(carried);
          // A carried walk still walks: `reset` zeroes her speed, so the shot's
          // own pace is what the legs and footfalls are told (`CarriedPose`).
          current = carried.speed !== undefined && carried.speed > 0
            ? { ...character.state, speed: carried.speed, gait: 'walk', grounded: true }
            : character.state;
          previous = current;
        }
      }
      if (beatPartModule !== null) {
        syncBeatPartTick(dt);
        beatPartTickMutable.called = input.pressed('callHolly');
        beatPartModule.fixedStep(beatPartTick);
      }

      // The beat runs on the FIXED step, between the character and physics:
      // a story that advances by wall time is a story whose timings differ on
      // a 60 Hz and a 144 Hz display, and the whole point of `BeatSequencer`
      // being deterministic is lost the moment it is fed a frame delta.
      //
      // ── The beat does not begin until the card has finished ────────────────
      //
      // Holding the SEQUENCER rather than the fade, and that is the whole trick.
      // A stage's `enter` is what emits `open`, and `open` is what tells the
      // director to fade in — so a beat that has not ticked yet is a beat still
      // sitting on the black `FadeOverlay` starts at. The card is opaque and
      // above it at z-index 6, the world is genuinely not being shown, and when
      // the card clears the beat opens exactly as it does on a cold boot.
      //
      // Fading the world in *behind* the card was the alternative and it is
      // strictly worse: the reveal would already be over by the time anybody
      // could see it.
      if (story && chapterCard === null) {
        story.update(dt, {
          completed: storyCompletions(),
          progress: storyProgress(),
          self: { x: current.x, y: current.y, z: current.z },
        });
        for (const cue of story.drainCues()) {
          if (traceCueIds !== null) traceCueIds.push(cue.id);
          stageCue(cue);
        }
      }
      if (story) {
        // After the story, so a stage that just ended hands the next stage's
        // goal in on the same tick and its stall clock starts from nothing.
        wayContextSelf.x = current.x;
        wayContextSelf.y = current.y;
        wayContextSelf.z = current.z;
        const way = chapterCard === null && !story.finished
          ? wayGoalFor(beatNumber, partNumber, story.stageId, wayContext)
          : null;
        wayfinder.setGoal(way?.key ?? null, way?.goal ?? null);
        wayfinder.step(dt, current);
        wayfinderTicked = true;
        for (const call of wayfinder.drainCalls()) {
          if (call.call !== undefined) audio.engine.playOneShot(call.call, { gain: 0.5, position: { x: call.x, y: call.y, z: call.z } });
        }
      }

      // ── Beat 10's one verb, and the sound it makes ────────────────────────
      //
      // `F` is read here with the rest of the edge-triggered actions, upstream
      // of the step, so `input.consume()` at the bottom of the tick still sees
      // it exactly once.
      const called = input.pressed('callHolly');
      // The one-shot teach, latched on the press rather than on the arrival:
      // the player has demonstrated they know the key, which is all the
      // prompt was ever for. See `promptedVerb` on the render step.
      if (called) callPromptTaught = true;
      // A part's half-steps either side of `physics.step` (beat 10: Holly and
      // her bell before; the dog's ray-cast watch and the queue after).
      if (beatPartModule?.beforePhysics !== undefined) {
        beatPartTickMutable.dt = dt;
        beatPartTickMutable.called = called;
        beatPartPlayer.x = current.x;
        beatPartPlayer.y = current.y;
        beatPartPlayer.z = current.z;
        beatPartModule.beforePhysics(beatPartTick);
      }
      if (input.pressed('muteAudio')) toggleMuted();
      physics.step(dt);
      if (beatPartModule?.afterPhysics !== undefined) {
        beatPartTickMutable.dt = dt;
        beatPartPlayer.x = current.x;
        beatPartPlayer.y = current.y;
        beatPartPlayer.z = current.z;
        beatPartModule.afterPhysics(beatPartTick);
      }

      // ── Stealth sees the world only after the world has been stepped ───────
      //
      // This is three lines below where it obviously belongs, and the reason is
      // a bug that cost most of an hour. `hasLineOfSight` is a Rapier `castRay`,
      // and Rapier's spatial acceleration structure is rebuilt inside
      // `world.step()`. On the **first** fixed tick of the beat the colliders
      // existed but the structure did not yet know about them, so the ray from
      // his eye to her back reported no hit — through 150 mm of end wall with
      // her two metres inside a different room.
      //
      // One tick of `lineOfSight: 1` is 0.023 of suspicion out of 100. It decays
      // to nothing two seconds later and it changes no outcome, which is exactly
      // why it is worth writing down: the same mistake in beat 4, where three
      // patrols query every tick and the player is being hunted, is a detection
      // nobody can explain. Spec §5.1 says an unexplainable detection is a bug,
      // and this is the shape one comes in.
      //
      // Everything the query READS was decided above — his position, the gate's
      // angle, hers. Only the asking moved.
      // A watched module room, stepped here for beat 6's reason: the ray query
      // reads Rapier's structure, rebuilt inside `world.step()`. Her footfalls
      // go in per stride on the room's own surface, beat 2's rule; what a
      // sighting costs is the room's call (`RoomStealth.afterStep`).
      if (roomModule?.stealth !== undefined && stealth) {
        const heardAs = heardGait(current.gait);
        if (herFootfalls.step(current.speed * dt, heardAs, current.grounded) && heardAs !== null) {
          stealth.emitSound({
            position: { x: current.x, y: current.y, z: current.z },
            radius: emissionRadius(roomModule.stealth.footfallSurface, heardAs),
          });
        }
        const snapshot = stealth.step(dt, { x: current.x, y: current.y, z: current.z });
        roomModule.stealth.afterStep(snapshot, (respawn) => {
          character.reset(respawn);
          current = character.state;
          previous = current;
        });
      }
      pausedSimTime += dt;
    } else if (input.pressed('muteAudio')) {
      toggleMuted();
    }

    if (activeTrace !== null) activeTrace.record(traceTick++, {
      simTime: pausedSimTime,
      paused,
      muted: audio.muted,
      character: current,
      basis,
      // A paused tick does not sample a movement basis. Its axis is still an
      // input fact, so read the pure Input query only while recording.
      moveAxis: sampledAxis ?? input.moveAxis(),
      down: TRACE_ACTIONS.filter((action) => input.down(action)),
      pressed: TRACE_ACTIONS.filter((action) => input.pressed(action)),
      interaction: interaction.snapshot,
      story: story === null
        ? { stageId: null, stageIndex: null, stageTime: null, cuesEmitted: null, finished: null }
        : { ...story.debug, finished: story.finished },
      cues: traceCueIds ?? [],
      bodies: traceBodyTransforms(),
      bodyCount: physics.world.bodies.len(),
      colliderCount: physics.world.colliders.len(),
      siblingPresent: roomModule?.farrowing !== undefined && roomModule.farrowing.bay.takenSibling.parent !== null,
    });
    // `consume()` clears edge state, so a trace after it could never say that
    // an action was pressed. Keep it last even while paused for the same
    // reason: held edges must not burst into unrelated actions on resume.
    input.consume();
  };

  loop = startLoop({
    fixedUpdate,
    render(alpha, frameDt) {
      renderer.info.reset();
      // A session that has rendered steadily for a few seconds is healthy, so
      // the reload budget resets. Without this, two unrelated context losses
      // hours apart would exhaust it and refuse to recover the second one.
      if (loop && loop.frameCount === 600) {
        try {
          window.sessionStorage.removeItem(RELOAD_KEY);
        } catch {
          // Ignore: storage being unavailable must never break the render step.
        }
      }
      // Smoothed so the number is readable rather than jittery.
      frameTimeMs += (frameDt * 1000 - frameTimeMs) * 0.05;
      // Bounded so a long session cannot grow this without limit.
      if (samples.length < 20_000) samples.push(frameDt * 1000);

      if (paused) {
        // Mouse/touch look is accumulated outside the fixed step. Throw it away
        // while paused so returning to the game cannot apply a large camera jump.
        input.takeLook();
        if (post) post.render(scene, camera.camera);
        else renderer.render(scene, camera.camera);
        return;
      }

      // Position and facing are interpolated; nothing else is. Gait is a
      // discrete state and blending it would produce values that are not gaits.
      const x = previous.x + (current.x - previous.x) * alpha;
      const y = previous.y + (current.y - previous.y) * alpha;
      const z = previous.z + (current.z - previous.z) * alpha;
      let dFacing = current.facing - previous.facing;
      // Shortest way round, so crossing ±π does not spin her the long way.
      if (dFacing > Math.PI) dFacing -= Math.PI * 2;
      if (dFacing < -Math.PI) dFacing += Math.PI * 2;
      const facing = previous.facing + dFacing * alpha;

      // The roll SPIN.
      //
      // `rollClip` is the tuck — legs folded, ears pinned, body compressed. The
      // rotation was always meant to live here so it could follow real speed
      // instead of a clip's authored rate, and it was missing: she slid along
      // the floor in a tuck pose without turning over, which reads exactly as
      // "flying" and not at all as rolling.
      //
      // One revolution per her own circumference, so the spin is locked to
      // distance travelled and cannot look like it is skidding.
      if (current.gait === 'roll') {
        rollSpin += (current.speed * frameDt) / ROLL_CIRCUMFERENCE * Math.PI * 2;
        // Wrapped, or a long uninterrupted roll grows it without bound and the
        // float precision of the rotation quietly degrades. Orientation is
        // unaffected: everything downstream takes a sin or a cos of it.
        rollSpin %= Math.PI * 2;
      } else {
        // Unwind to upright rather than snapping, or leaving a roll pops her.
        rollSpin *= Math.exp(-12 * frameDt);
        if (Math.abs(rollSpin) < 0.001) rollSpin = 0;
      }

      rosalieRoot.position.set(x, y, z);
      rosalieRoot.rotation.y = facing;
      // Pitch about her own left-right axis. It is a child of the yaw node, so
      // she rolls in the direction she is travelling rather than about a world
      // axis, and it pivots on her centre rather than on her feet.
      rollNode.rotation.x = rollSpin;
      // Raise the axis so her long side just clears the floor. See rollAxisHeight.
      rollNode.position.y = rollAxisHeight(rollSpin);

      // Animation runs on the RENDER step, not the fixed step. It is
      // presentation: it reads simulation state and never writes to it, so
      // running it at display rate costs nothing and looks smoother.
      animState.gait = current.gait;
      animState.speed = current.speed;
      animState.grounded = current.grounded;
      animState.turnRate = current.turnRate;
      animState.slopeGrade = current.slopeGrade;
      animState.landingImpact = pendingLandingImpact;
      pendingLandingImpact = 0;
      animation.update(frameDt, animState, pose);
      // Her snout points at whatever she could act on, which is D-002's
      // amendment working as designed and spec §9's contextual prompt made out
      // of a body part instead of an icon. This replaces the hand-driven target
      // the handle has carried since M0a — that seam stays for the harness, but
      // it is no longer the only thing writing here, so the interaction system
      // wins whenever it has an opinion.
      const snoutTarget = interaction.perceptionTarget();
      // D-209's look beats the snout. It only fires after a stall, and never
      // at a goal she has reached, so the thing it overrides is always an
      // interactable she is in reach of and NOT using — beat 7's boards after
      // a failed push are the case, and with the snout first the look never
      // showed (frame `b7-tried-look`, session 71). It lasts `lookHold`.
      const wayTarget = wayfinder.attention();
      if (wayTarget) perception.setTarget(wayTarget);
      else if (snoutTarget) perception.setTarget(snoutTarget);
      else if (manualPerception) perception.setTarget(manualPerception);
      else perception.setTarget(null);
      perception.apply(frameDt, x, y, z, facing, pose);
      roomModule?.posePatch?.(pose);
      rig.apply(pose);

      // Presentation, on the render step, like every other fade in the game. It
      // disposes itself the moment it is done rather than lingering as a
      // transparent element: `pointer-events` is already none, but an overlay
      // that outlives its own animation is how the next one ends up underneath.
      if (chapterCard !== null) {
        chapterCard.update(frameDt);
        if (chapterCard.done) {
          chapterCard.dispose();
          chapterCard = null;
        }
      }

      renderHer.x = x;
      renderHer.y = y;
      renderHer.z = z;
      beatPartModule?.renderStep(frameDt, renderHer);
      // **This line was missing for the whole of beat 6's first build and the
      // beat was a black screen.** The director's `update` is what interpolates
      // the fade, and `FadeOverlay` exists at full black from the frame it is
      // constructed — so `open` set a target that nothing ever moved toward.
      // Every assertion the beat had was green: the storm ran, the exposure
      // delta was exact, capture armed, and she arrived under the trailer.
      //
      // Nothing checkable was wrong. It took a screenshot, which is the whole of
      // why this project generates them (D-048, and the standing rule in
      // `CLAUDE.md` about never marking anything done because the code looks
      // correct).
      // ── One beat hands over to the next ──────────────────────────────────
      //
      // Until this existed, beat 1 ended by fading to black and STAYING there.
      // The sequencer finished, the overlay held at 1, and the game sat on a
      // black screen for as long as you let it — every beat after the first was
      // reachable only by typing `?beat=N` into the address bar, which meant the
      // one thing three authored beats are FOR, playing Act 1, was the one thing
      // the build could not do. It is the first thing Alex hit on playing it.
      //
      // **The handover is a navigation, not an in-place swap, and that is the
      // decision here.** Beats do not share a world: beat 2 removes the hall's
      // shut gate collider and rebuilds it as a hinged one, adds a worker and a
      // stealth field, and re-instances the gate out of the merged dressing;
      // beat 3 is in a different room altogether. Swapping any of that inside a
      // running loop would be a SECOND construction path for a state that today
      // has exactly one — `boot()` — and it would be the path no spec covers,
      // while every `?beat=N` spec in the suite already covers this one.
      //
      // The seam is hidden by the two things that were already true: a beat ends
      // on black, and the next one *opens* on black because `FadeOverlay` is
      // opaque from the moment the element exists (see the comment on its
      // `#value`). What it costs is real and small — pointer lock and the audio
      // unlock are both lost across a navigation, exactly as at a cold boot, so
      // the player clicks once. `docs/backlog.md` files the seamless version
      // against the beat that first cannot afford that click.
      //
      // Presentation, on the render step, on purpose: the fade this waits on is
      // a render-step value (D-045), and nothing about leaving the page belongs
      // in a fixed simulation tick.
      // ── A section boundary commits ────────────────────────────────────────
      //
      // D-053: finishing a beat writes the checkpoint, and nothing else does.
      // This is deliberately OUTSIDE the chain block below: a beat that has no
      // successor still finished, a beat played with `?chain=0` still finished,
      // and a checkpoint that only existed for beats that happen to hand over
      // would silently never record the end of the act.
      // The stopwatch, on the render step with the render delta: the question it
      // answers is "did that drag?", which is about the player's afternoon and
      // not about the fixed step.
      timeline.sample(frameDt, story?.debug.stageId ?? null);

      if (!boundaryCommitted && story?.finished === true) {
        boundaryCommitted = true;
        commitBoundary(beatNumber, partNumber, ledger.snapshot(), beatPartModule?.outcome?.());
      }

      // Where a finished beat goes. The successor if there is one; otherwise the
      // shell, which plays the credits and comes to rest on the title.
      //
      // **The act used to end by staying on black.** Beat 7 has no `next`, so
      // the block below simply never fired and the game sat there — the same
      // failure the hand-over was built to delete, one beat further along.
      // `?credits=1` is the only thing the shell is ever told, and it is a fact
      // about how the player arrived rather than an instrument like `trace`.
      //
      // `chainWanted` guards both halves. Every spec but `beat-chain.spec.ts`
      // boots with `?chain=0` precisely so a navigation cannot happen out from
      // under its assertions in the last seconds of a long test.
      const handover =
        chainNextPart !== null
          ? `${window.location.pathname}?beat=${beatNumber}&part=${partNumber + 1}`
          : chainNext !== undefined
          ? `${window.location.pathname}?beat=${chainNext}&chained=1`
          : chainWanted && beatWanted
            ? `${window.location.pathname}?credits=1`
            : null;

      if (handover !== null && !chaining && story?.finished === true) {
        sinceBeatEnded += frameDt;
        const fade = beatPartModule?.fade ?? 0;
        if (fade >= 0.99 || sinceBeatEnded >= CHAIN_MAX_WAIT) {
          chaining = true;
          // ── The next URL is built from nothing but the beat number ────────
          //
          // It carried the current query string forward at first, which is the
          // obvious thing and is wrong. Found on independent review: `?room=`
          // wins over the beat's own room, so `?room=farrowing&beat=2` chained
          // into `?room=farrowing&beat=3` — the fattening hall's beat in the
          // farrowing hall, where Otto is not built, his part is never
          // constructed and **the beat silently does not run**. That is the
          // exact failure this whole mechanism exists to delete, rebuilt one
          // parameter along.
          //
          // Dropping `room` alone would have fixed the reported case and left
          // the shape of it: `trace`, `script`, `battens` and `post` are all
          // instruments aimed at the boot somebody typed, and re-arming one for
          // a beat nobody pointed it at is the same bug wearing a different
          // name. So the successor's URL states the successor and nothing else,
          // and there is no list here to keep in step with the parameters.
          //
          // `replace`, not `assign`: the back button should leave the game, not
          // walk the player backwards into the beat they have just finished and
          // then immediately forwards again when it ends.
          // `chained=1` is the ONE thing the successor is told beyond its own
          // number, and it is not an instrument like `trace` or `battens` — it
          // is a fact about how the player got here. A beat opened by hand has
          // no passage to mark; a beat handed over to does. See `chapterCard`.
          window.location.replace(handover);
        }
      }

      const interactionSnapshot = interaction.snapshot;
      const actInteractable = interactionSnapshot.actId === null
        ? undefined
        : interactables.find((interactable) => interactable.id === interactionSnapshot.actId);
      const actVerb: 'nudge' | 'root' | null = interactionSnapshot.actId === null
        ? null
        : actInteractable?.verbs.includes('root') === true && !interaction.completed(interactionSnapshot.actId)
          ? 'root'
          : 'nudge';
      const observing = interactionSnapshot.progressVerb === 'observe' && interactionSnapshot.progress > 0;
      const verb = observing
        ? 'observe'
        : actVerb ?? (interactionSnapshot.observeId === null ? null : 'observe');
      // ── CALL is offered once, and only when nothing else is ──────────────
      //
      // Two rules, and both are about not becoming a HUD.
      //
      // **A reach verb wins**, and `verb === null` is the test for that rather
      // than `actId === null`. The first version checked only the act verb, so
      // CALL painted over WATCH — including over a hold already in progress,
      // where the prompt is carrying a progress meter the player is watching.
      //
      // **It teaches once.** `F` is the verb the rest of this act is built on,
      // so it has to be discoverable; it does not have to be permanent. The
      // latch is set the first time the player presses it in this boot and is
      // never cleared. On touch the button stays, because a phone has no key to
      // discover (D-125).
      const moduleCall = beatPartModule?.callVerb ?? roomModule?.callVerb ?? null;
      if (notesRecorded > notesAnnounced) {
        notesAnnounced = notesRecorded;
        notesPromptSeconds = NOTES_PROMPT_SECONDS;
      }
      // CALL keeps its priority: it is the verb the act is built on. NOTES only
      // fills an empty slot, and only just after she has written something down.
      const callPrompted = moduleCall !== null && verb === null && !callPromptTaught;
      const notesEligible = !callPrompted && verb === null && raisedSheet === null;
      // The clock only runs while NOTES could be shown: started on the note, it
      // spent its five seconds behind the WATCH still offered for the thing she
      // had just finished watching, and the player never saw it (review, D-237).
      if (notesEligible) notesPromptSeconds = Math.max(0, notesPromptSeconds - frameDt);
      const promptedVerb = callPrompted ? 'call' : notesEligible && notesPromptSeconds > 0 ? 'notes' : verb;
      promptState = {
        verb: promptedVerb,
        progress: interactionSnapshot.progressVerb === promptedVerb ? interactionSnapshot.progress : 0,
        // Any camera the player is not driving suppresses the prompt: paused,
        // the beat's own reveal, an authored shot (which is what the harness
        // takes turnarounds through), or most of the way into a fade.
        suppressed: paused
          || camera.overridden
          // Beat 5 takes it for the ride and never gives it back, so both of
          // its suppressors are here rather than one.
          || beatPartModule?.promptSuppressed === true
,
        touch: touch.enabled,
      };
      verbPrompt.update(promptState);

      // ── Her map: its own key, and still what `Q` does with nothing in reach
      //
      // Spec §5.3 puts both on a held `Q`, and for eight beats that was the
      // only way up: with something in reach she watches it, with nothing in
      // reach the sheet comes up. It made the map unreachable for all of beat
      // 10 (the herd is always in reach) and dropped it mid-hold whenever
      // something walked into reach, so it has a key of its own now (D-237)
      // and `Q` keeps the old rule as a second way in.
      //
      // `promptState.suppressed` is reused deliberately instead of restating
      // its list of conditions. It already means "the player is not driving
      // this camera" — paused, an authored framing, a beat's own fade — and
      // every one of those is a moment a map must not appear in. A second
      // derivation of that list is the failure `wearForBeat` exists to prevent.
      //
      // **Beat 11's mirror is the one exception, and it is the same key.** At
      // his wall, `observe` has a target, and holding it raises HIS sheet —
      // ruled, dated, with her hedgerow route on it — under exactly the
      // conditions hers comes up in, so the two are compared like for like
      // (`docs/beat-11-plan.md`). Which sheet is up is remembered, so stepping
      // from the wall to open floor with `Q` held swaps one for the other.
      const hisWanted = input.down('observe')
        && interactionSnapshot.observeId === OFFICE_WALL_MAP_ID
        && !promptState.suppressed;
      // `notes` is her map's own key (D-237) and raises it whatever is in
      // reach; `observe` still does when nothing is, which is how it was found
      // before the key existed. Only her sheet — his is on the wall.
      const mapWanted = hisWanted || ((input.down('notes')
        || (input.down('observe') && interactionSnapshot.observeId === null))
        && !promptState.suppressed);
      // Her own key always means her own sheet, even with `Q` held at his wall.
      const sheetWanted = !mapWanted ? null : hisWanted && !input.down('notes') ? 'his' : 'hers';
      if (sheetWanted !== raisedSheet) {
        if (sheetWanted === 'his') mentalMap.raise(hisMapSheet(ledger.wash(HIS_MAP_ZONE), WASH_CELL_METRES));
        else if (sheetWanted === 'hers') mentalMap.raise(mapSheet());
        else mentalMap.lower();
        raisedSheet = sheetWanted;
      }
      mentalMap.update(frameDt, { x, z });

      touch.setVerbAvailability({
        interact: actVerb,
        // NOTES has its own button now (D-237), so WATCH is only offered when
        // there is something to watch, like NUDGE.
        observe: interactionSnapshot.observeId === null ? null : 'observe',
        call: moduleCall,
        // Which control holds a sheet up, so the touch layer keeps that one
        // and clears the other verbs out of the sheet's way. Her own key wins
        // the tie, as it does for which sheet is raised.
        sheet: raisedSheet === null ? null : input.down('notes') ? 'notes' : 'observe',
      });

      // Straw in a pen, concrete on the walkway: the room's call, and the same
      // answer `cameraModeFor` reads (`RoomModule.underfoot`). A room with one
      // floor leaves the footfall surface alone.
      const ground = roomModule?.underfoot?.(x, z);
      if (ground !== undefined) audio.surface = ground.surface;

      const look = input.takeLook();
      camera.addLook(look.dx, look.dy);
      camera.setLookHeld(touch.lookHeld);
      // A beat's authored shot. It goes through `overrideShot` for exactly the
      // reason the handle's `shot()` does: setting `camera.position` here is
      // silently overwritten by the spring arm on the very next frame. This is
      // the one place a beat's camera enters the render step.
      //
      // The part's shot first, then the room's observe framing. A nearby
      // observe target merely supplies a prompt: the room may own the lens
      // only while Q is actively advancing that observe; otherwise walking
      // near the desk would freeze the camera without any player action.
      // Eased in, except a part whose shots cut (`framingCuts`: beats 1, 5, 6
      // and 7, whose shots were legacy directors' — an eased version of a cut
      // is a different shot).
      const partFraming = beatPartModule?.framing?.(renderHer) ?? null;
      const framing = partFraming ?? roomModule?.framing(observing ? interactionSnapshot.observeId : null) ?? null;
      const easedFraming = partFraming !== null && beatPartModule?.framingCuts === true ? null : framing;
      if (framing) {
        // A portrait phone gets the shot's portrait composition where it has
        // one; a rotation mid-shot moves to it on the next frame.
        const lens = framingForAspect(framing, camera.camera.aspect);
        const position = shotPosition.set(lens.position.x, lens.position.y, lens.position.z);
        const lookAt = shotLookAt.set(lens.lookAt.x, lens.lookAt.y, lens.lookAt.z);
        if (framing === easedFraming) camera.overrideShotEased(position, lookAt, lens.fov, frameDt);
        else camera.overrideShot(position, lookAt, lens.fov);
        beatHoldsCamera = true;
      } else if (beatHoldsCamera) {
        beatHoldsCamera = false;
        // Eased, not cut. Spec §4: the transition between a cinematic framing
        // and the rig is always a cut or an eased move, never a snap — and a cut
        // here would land on the back of her head from a metre away.
        //
        // Every beat gets this, not just beat 1's. `framing()` returning null
        // IS the hand-back; see `beatHoldsCamera` for the beats this branch
        // was once not reaching.
        camera.releaseShot(true);
      }
      camera.setMode(cameraModeFor(current, x, z));
      camera.setTarget(x, y, z, facing);
      // Spec §7's `focus`, D-066. `progressVerb === 'observe'` is precisely
      // "holding `Q` at something watchable" — the interaction system already
      // resolves the cone, the reach and the verb list — and the position comes
      // from the system rather than from the beat's declared list because the
      // worker moves (see `positionOf`).
      //
      // Presentation, and on the render step on purpose: nothing in the
      // simulation reads the camera, so a rig that reframes at display rate
      // stays out of the golden trace.
      camera.setFocus(
        interactionSnapshot.progressVerb === 'observe' && interactionSnapshot.observeId !== null
          ? interaction.positionOf(interactionSnapshot.observeId)
          : null,
      );
      camera.update(frameDt, sweep);

      // AFTER the camera has settled, because the listener rides the lens and a
      // frame of lag between what you see and where you hear it from is audible
      // as a smear when the camera whips.
      camera.camera.getWorldDirection(listenerForward);
      listenerPosition.x = camera.camera.position.x;
      listenerPosition.y = camera.camera.position.y;
      listenerPosition.z = camera.camera.position.z;
      audioFrame.gait = current.gait;
      audioFrame.speed = current.speed;
      audioFrame.grounded = current.grounded;
      // The animator has already consumed and cleared the latch, so read the
      // value that was handed to it rather than the now-zeroed one.
      audioFrame.landingImpact = animState.landingImpact;
      audioFrame.scramble = current.scramble;
      audioFrame.cycles = animation.debug.cycles;
      audioFrame.position.x = x;
      audioFrame.position.y = y;
      audioFrame.position.z = z;
      audio.update(frameDt, audioFrame, {
        position: listenerPosition,
        forward: listenerForward,
        up: LISTENER_UP,
      });

      // `listenerPosition` is the settled lens this frame, which is what the
      // farrowing hall's batten cull sorts by (`RoomView`).
      if (roomModule !== null) roomModule.renderStep(frameDt, roomView);

      // Beat 10's bell strokes, the dog's snuffs and Holly's body — after the
      // listener has moved (`BeatPartModule.renderLate`).
      beatPartModule?.renderLate?.(frameDt);

      // The weather is PRESENTATION and it advances on the render step at
      // display rate, never in `fixedUpdate` — the same rule the animation, the
      // roll spin and the camera keep. Nothing reads it back, so it cannot
      // affect the simulation, and a dropped frame costs a millimetre of rain
      // rather than a divergence.
      //
      // `storm.flash` is the opposite and belongs to the fixed step, because
      // the stealth system samples it; when beat 6's director exists it writes
      // that value and this line will read it (D-107).
      if (post && weatherActive) post.advanceRain(frameDt);

      if (post) post.render(scene, camera.camera);
      else renderer.render(scene, camera.camera);
    },
  });

  window.__rosalie = {
    scene,
    get camera() {
      return camera.camera;
    },
    renderer,
    input,
    trace,
    physics,
    get frameCount() {
      return loop?.frameCount ?? 0;
    },
    get simTime() {
      return pausedSimTime;
    },
    get frameTimeMs() {
      return frameTimeMs;
    },
    get renderStats() {
      return {
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
      };
    },
    frameSamples: () => [...samples],
    resetFrameSamples: () => {
      samples = [];
    },
    get character() {
      return current;
    },
    get cameraMode() {
      return cameraModeFor(current, current.x, current.z);
    },
    get cameraDebug() {
      return {
        mode: camera.mode,
        yaw: camera.yaw,
        rise: camera.rise,
        armDistance: camera.armDistance,
        position: {
          x: camera.camera.position.x,
          y: camera.camera.position.y,
          z: camera.camera.position.z,
        },
        fov: camera.camera.fov,
        focusWeight: camera.focusWeight,
        overridden: camera.overridden,
        subjectNdc: camera.subjectNdc,
      };
    },
    get weatherDebug() {
      // This is plumbing evidence. `rain-direction.spec.ts` separately proves
      // that the pixels move; this makes the same conditional fail if either
      // beat forgets to advance the real post stack (D-100).
      return {
        rainTime: post?.rainTime ?? 0,
        rainDensity: post?.rainDensity ?? 0,
      };
    },
    get postDebug() {
      return post === null ? null : { chromaCeiling: post.chromaCeiling };
    },
    three: {
      Box3: THREE.Box3,
      Frustum: THREE.Frustum,
      Matrix4: THREE.Matrix4,
      Vector3: THREE.Vector3,
    },
    setCameraTuning(mode, patch) {
      Object.assign(camera.tuning.modes[mode], patch);
      if (mode === camera.mode) camera.refreshMode();
    },
    setYaw(radians) {
      camera.setYaw(radians);
    },
    respawn(x, y, z, facing) {
      // Guarded because this is reachable from the console and from the test
      // harness. A non-finite value here propagates straight into a transform
      // and then into a matrix, and a NaN matrix can take a driver down with it.
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        throw new Error(`respawn: non-finite position (${x}, ${y}, ${z})`);
      }
      if (facing !== undefined && !Number.isFinite(facing)) {
        throw new Error(`respawn: non-finite facing (${facing})`);
      }
      character.reset(facing === undefined ? { x, y, z } : { x, y, z, facing });
      // Beat 10 goes back to its start — everything but the dog, which is
      // deliberate and load-bearing (`HedgerowNight.resetForRespawn`).
      beatPartModule?.resetForRespawn?.();
      previous = character.state;
      current = character.state;
    },
    teleportPlayer(x, y, z, facing) {
      // This is reachable from the console and the test harness just as
      // `respawn` is. Keeping the same finite boundary prevents one queued NaN
      // from crossing a tick boundary and poisoning the controller transform.
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        throw new Error(`teleportPlayer: non-finite position (${x}, ${y}, ${z})`);
      }
      if (facing !== undefined && !Number.isFinite(facing)) {
        throw new Error(`teleportPlayer: non-finite facing (${facing})`);
      }
      if (pendingTeleport !== null) throw new Error('teleportPlayer: teleport already pending');
      pendingTeleport = facing === undefined ? { x, y, z } : { x, y, z, facing };
    },
    pauseSimulation() {
      loop?.pauseFixedUpdates();
    },
    stepSimulation(steps) {
      if (!loop) throw new Error('stepSimulation: loop is not running');
      loop.stepFixedUpdates(steps);
    },
    resumeSimulation() {
      loop?.resumeFixedUpdates();
    },
    shot(position, lookAt, fov = ROSALIE_CAMERA.modes.follow.fov) {
      // Same reasoning as respawn: this is reachable from the console and the
      // harness, and a non-finite camera transform is a NaN matrix.
      const ok = [position.x, position.y, position.z, lookAt.x, lookAt.y, lookAt.z, fov]
        .every((v) => Number.isFinite(v));
      if (!ok) throw new Error('shot: non-finite framing');
      camera.overrideShot(
        shotPosition.set(position.x, position.y, position.z),
        shotLookAt.set(lookAt.x, lookAt.y, lookAt.z),
        fov,
      );
    },
    releaseShot(eased = false) {
      camera.releaseShot(eased);
    },
    setPerceptionTarget(target) {
      // Validates EVERY field, not just the coordinates. The first version
      // checked x/y/z only, and the two it missed were the dangerous ones:
      // a non-finite `weight` survives the solver's clamp — Math.min/max of NaN
      // is NaN — and poisons the head and snout transforms every frame; an
      // unknown `kind` indexes the gain table to `undefined` and then THROWS,
      // inside the render loop, on every frame until the target is cleared.
      if (target) {
        const finite = Number.isFinite(target.x)
          && Number.isFinite(target.y)
          && Number.isFinite(target.z)
          && Number.isFinite(target.weight);
        if (!finite) throw new Error('setPerceptionTarget: non-finite field');
        if (!PERCEPTION_KINDS.includes(target.kind)) {
          throw new Error(`setPerceptionTarget: unknown kind "${target.kind}"`);
        }
      }
      manualPerception = target;
    },
    get officeDebug() {
      const diagnostics = roomModule?.diagnostics();
      return diagnostics?.kind === 'office' ? diagnostics.kestner : null;
    },
    get woolShedDebug() {
      const diagnostics = roomModule?.diagnostics();
      return diagnostics?.kind === 'wool-shed' ? diagnostics.woolShed : null;
    },
    get yardDawnDebug() {
      const diagnostics = roomModule?.diagnostics();
      return diagnostics?.kind === 'yard-dawn' ? diagnostics.yardDawn : null;
    },
    get roomDebug() { return roomModule?.diagnostics() ?? null; },
    get stealthDebug() {
      const snapshot = stealth?.snapshot();
      return snapshot === undefined ? null : { watchers: snapshot.watchers.length, alarmLevel: snapshot.alarmLevel };
    },
    get hedgerowDebug() {
      const diagnostics = roomModule?.diagnostics();
      if (diagnostics?.kind !== 'hedgerow') return null;
      return { ...diagnostics.hedgerow, backgroundMeanLuminance: bakedSkyMeanLuminance(roomSky?.background ?? null) };
    },
    get twelveOutDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'twelve-out' ? diagnostics.twelveOut : null;
    },
    get hollyDebug() {
      const diagnostics = roomModule?.diagnostics();
      return diagnostics?.kind === 'wool-shed' ? diagnostics.holly : null;
    },
    get animationDebug() {
      return animation.debug;
    },
    get wayfindingDebug() {
      return wayfinderTicked ? wayfinder.debug : null;
    },
    get storyDebug() {
      if (!story) return null;
      return { ...story.debug, finished: story.finished };
    },
    get blackoutDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'blackout-hall' ? diagnostics.blackout : null;
    },
    get beatDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'born' ? diagnostics.born : null;
    },
    get ottoDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'otto' ? diagnostics.otto : null;
    },
    get latchDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'latch' ? diagnostics.latch : null;
    },
    get corridorDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'corridor' ? diagnostics.corridor : null;
    },
    get cullDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'cull' ? diagnostics.cull : null;
    },
    get outDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'out' ? diagnostics.out : null;
    },
    get yardDebug() {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'yard' ? diagnostics.yard : null;
    },
    yardExposureAt(x, z) {
      const diagnostics = beatPartModule?.diagnostics?.(current);
      return diagnostics?.kind === 'yard' ? diagnostics.exposureAt(x, z) : null;
    },
    get interactionDebug() {
      const snap = interaction.snapshot;
      return { actId: snap.actId, observeId: snap.observeId, progress: snap.progress };
    },
    ledgerDebug() {
      const snapshot = ledger.snapshot();
      return {
        observed: [...snapshot.observed],
        washCells: Object.values(snapshot.wash).reduce((total, cells) => total + cells.length, 0),
        zones: [...ledger.zones()],
      };
    },
    get promptDebug() {
      return { ...promptState };
    },
    skipToStage(stageId) {
      if (!story) throw new Error('skipToStage: no beat is running (did you boot with ?beat=0?)');
      story.skipTo(stageId);
      for (const cue of story.drainCues()) stageCue(cue);
    },
    get audioDebug() {
      return {
        started: audio.started,
        muted: audio.muted,
        contextState: audio.engine.contextState,
        activeVoices: audio.engine.activeVoices,
        cachedSounds: audio.engine.cachedSounds,
        oneShotsStarted: audio.engine.oneShotsStarted,
        oneShotStartsById: Object.fromEntries(audio.engine.oneShotStartsById),
        lastError: audio.engine.lastError,
        suppressed: audio.engine.suppressedIds,
        ambience: audio.activeBedIds,
      };
    },
    buildId: __BUILD_ID__,
  };
}

void boot().catch((err: unknown) => {
  // A blank canvas is what this used to produce, which is indistinguishable
  // from "still loading" and from a dead GPU. Say so on screen.
  console.error('[rosalie] boot failed', err);
  showFailure(
    'Rosalie could not start.',
    err instanceof Error ? err.message : String(err),
  );
});
